import { useState, useMemo, useCallback, useEffect } from "react";
import { supabase } from "./supabase";

// ─── Constants ───
const UK_SDLT_RATES = [
  { threshold: 250000, rate: 0, surcharge: 0.05 },
  { threshold: 925000, rate: 0.05, surcharge: 0.10 },
  { threshold: 1500000, rate: 0.10, surcharge: 0.15 },
  { threshold: Infinity, rate: 0.12, surcharge: 0.17 },
];

const TABS = [
  { id: "dashboard",  label: "Dashboard",     icon: "⚡" },
  { id: "contacts",   label: "Contacts",       icon: "👥" },
  { id: "portfolio",  label: "Properties",     icon: "🏘" },
  { id: "investors",  label: "Investors",      icon: "💼" },
  { id: "deals",      label: "Deals",          icon: "📋" },
  { id: "scout",      label: "Market Scout",   icon: "🔍" },
  { id: "calc",       label: "Calculators",    icon: "🧮" },
  { id: "valuation",  label: "Valuation",      icon: "📍" },
  { id: "auction",    label: "Auction",        icon: "🔨" },
];

const PIPELINE_STAGES = [
  { id: "researching", label: "Researching", color: "#a2a2c0" },
  { id: "offer",       label: "Offer",       color: "#facc15" },
  { id: "in_legal",    label: "In Legal",    color: "#60a5fa" },
  { id: "completed",   label: "Exchanged / Completed",   color: "#4ade80" },
  { id: "archived",    label: "Archived",    color: "#6b7280" },
];
// Map legacy stage IDs to consolidated ones
const STAGE_ALIASES = { viewing: "researching", offer_made: "offer", under_offer: "offer", exchanged: "completed" };
const normalizeStage = (s) => STAGE_ALIASES[s] || s || "researching";

const PROPERTY_TYPES = ["Terraced House", "Semi-Detached", "Detached", "Flat / Apartment", "Bungalow", "HMO", "Commercial", "Land", "New Build"];
const STRATEGIES = ["Fix & Flip", "Buy & Hold", "BRRRR", "Rent-to-Rent", "Lease Option", "HMO Conversion", "Serviced Accommodation", "Development", "Below Market Value"];

const TYPE_ICONS = { call: "📞", email: "📧", sms: "💬", viewing: "🏠", meeting: "🤝", offer: "💰", letter: "✉️", note: "📋", legal: "⚖️", survey: "🔍" };
const OUTCOME_COLORS = { positive: "#4ade80", neutral: "#a2a2c0", negative: "#f87171", no_answer: "#facc15", callback_requested: "#fb923c" };

// ─── Utilities ───
const fmt = (n) => {
  if (n == null || isNaN(n)) return "—";
  return "£" + Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 });
};
const pct = (n) => (n == null || isNaN(n) ? "—" : n.toFixed(1) + "%");

function calcSDLT(price, isAdditional = true) {
  let tax = 0;
  let prev = 0;
  for (const band of UK_SDLT_RATES) {
    const rate = isAdditional ? band.surcharge : band.rate;
    const taxable = Math.min(price, band.threshold) - prev;
    if (taxable > 0) tax += taxable * rate;
    prev = band.threshold;
    if (price <= band.threshold) break;
  }
  return Math.round(tax);
}

async function callClaude(apiKey, systemPrompt, userMessage) {
  if (!apiKey) throw new Error("Please add your Anthropic API key in Settings.");
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 4096,
      system: systemPrompt,
      messages: [{ role: "user", content: userMessage }],
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `API error ${res.status}`);
  }
  const data = await res.json();
  return data.content?.map((b) => b.text || "").join("\n") || "No response.";
}

// ─── Property Data APIs ───
function extractPostcode(text) {
  const match = text.match(/[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}/i);
  return match ? match[0].toUpperCase() : null;
}

async function fetchSoldPrices(postcode) {
  const url = `https://landregistry.data.gov.uk/data/ppi/transaction-record.json?propertyAddress.postcode=${encodeURIComponent(postcode)}&_pageSize=10&_sort=-transactionDate`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Land Registry: ${res.status}`);
  const data = await res.json();
  return data.result?.items || [];
}

async function fetchEPC(postcode, email, apiKey) {
  const creds = btoa(`${email}:${apiKey}`);
  const url = `https://epc.opendatacommunities.org/api/v1/domestic/search?postcode=${encodeURIComponent(postcode)}&size=5`;
  const res = await fetch(url, {
    headers: { Accept: "application/json", Authorization: `Basic ${creds}` },
  });
  if (!res.ok) throw new Error(`EPC API: ${res.status}`);
  const data = await res.json();
  const cols = data["column-names"] || [];
  return (data.rows || []).map((row) => {
    const obj = {};
    cols.forEach((col, i) => { obj[col] = row[i]; });
    return obj;
  });
}

async function fetchCompany(query) {
  // Route through CRA dev proxy or Vercel serverless function — API key handled server-side
  const url = `/api/ch/search/companies?q=${encodeURIComponent(query)}&items_per_page=5`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Companies House: ${res.status}`);
  const data = await res.json();
  return data.items || [];
}

async function fetchFloodRisk(postcode) {
  const clean = postcode.replace(/\s+/g, "");
  // postcodes.io expects the standard format with a space (e.g. "SW1A 1AA")
  const normalised = clean.slice(0, -3) + " " + clean.slice(-3);
  const pcRes = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(normalised)}`);
  if (!pcRes.ok) throw new Error(`Postcode lookup failed (${pcRes.status})`);
  const pcData = await pcRes.json();
  if (!pcData.result) throw new Error("Postcode not found");
  const { latitude, longitude } = pcData.result;
  const floodRes = await fetch(
    `https://environment.data.gov.uk/flood-monitoring/id/floodAreas?lat=${latitude}&long=${longitude}&dist=1`
  );
  if (!floodRes.ok) throw new Error(`Flood API: ${floodRes.status}`);
  const floodData = await floodRes.json();
  const areas = floodData.items || [];
  return { latitude, longitude, floodAreas: areas, hasRisk: areas.length > 0 };
}

async function fetchHPI(locationQuery) {
  const searchRes = await fetch(
    `https://landregistry.data.gov.uk/data/ukhpi/region.json?_search=${encodeURIComponent(locationQuery)}&_pageSize=5`,
    { headers: { Accept: "application/json" } }
  );
  if (!searchRes.ok) throw new Error(`HPI search: ${searchRes.status}`);
  const searchData = await searchRes.json();
  const regions = searchData.result?.items || [];
  if (!regions.length) throw new Error("No matching HPI region found");
  const regionId = regions[0]["@id"] || "";
  const regionSlug = regionId.split("/").pop();
  const regionLabel = Array.isArray(regions[0].label) ? regions[0].label[0] : (regions[0].label || regionSlug);
  const now = new Date();
  for (let i = 3; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    try {
      const dataRes = await fetch(
        `https://landregistry.data.gov.uk/data/ukhpi/region/${regionSlug}/month/${ym}.json`,
        { headers: { Accept: "application/json" } }
      );
      if (!dataRes.ok) continue;
      const data = await dataRes.json();
      const item = data.result?.primaryTopic;
      if (!item) continue;
      return {
        region: regionLabel, month: ym,
        averagePrice: item.averagePrice,
        annualChange: item.percentageAnnualChange,
        monthlyChange: item.percentageChange,
        salesVolume: item.salesVolume,
        detached: item.averagePriceDetached,
        semiDetached: item.averagePriceSemiDetached,
        terraced: item.averagePriceTerraced,
        flat: item.averagePriceFlatMaisonette,
      };
    } catch { continue; }
  }
  throw new Error("No recent HPI data available for this area");
}

const EPC_COLORS = { A: "#22c55e", B: "#86efac", C: "#a3e635", D: "#facc15", E: "#fb923c", F: "#f87171", G: "#ef4444" };

// ─── Shared UI Components ───
function Field({ label, value, onChange, placeholder, type = "text", textarea, suffix, prefix }) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const resolvedType = isPassword ? (showPassword ? "text" : "password") : type;
  const base =
    "w-full bg-[#1f2135] border border-[#3e4060] rounded-lg px-4 py-3 text-[#f5f4ff] placeholder-[#444] text-sm focus:outline-none focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a22744] transition-all";
  return (
    <div className="space-y-1.5">
      {label && <label className="text-[11px] font-semibold tracking-[.15em] uppercase text-[#b8b8d0]">{label}</label>}
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8b8d0] text-sm">{prefix}</span>}
        {textarea ? (
          <textarea className={base + " min-h-[100px] resize-none"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        ) : (
          <input type={resolvedType} className={base + (prefix ? " pl-7" : "") + (suffix || isPassword ? " pr-10" : "")} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        )}
        {isPassword && (
          <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a6a82] hover:text-[#b8b8d0] transition-colors">
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            )}
          </button>
        )}
        {!isPassword && suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b8b8d0] text-xs">{suffix}</span>}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      {label && <label className="text-[11px] font-semibold tracking-[.15em] uppercase text-[#b8b8d0]">{label}</label>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#1f2135] border border-[#3e4060] rounded-lg px-4 py-3 text-[#f5f4ff] text-sm focus:outline-none focus:border-[#c9a227]"
      >
        {options.map((o) => (
          <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
            {typeof o === "string" ? o : o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function GoldButton({ onClick, loading, label, loadingLabel = "Analysing…", disabled, small }) {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={`w-full rounded-lg font-bold tracking-wider uppercase text-sm transition-all duration-300 disabled:opacity-40 ${small ? "py-2.5 text-xs" : "py-3.5"}`}
      style={{
        background: loading || disabled ? "#373954" : "linear-gradient(135deg, #c9a227 0%, #d4af37 50%, #c9a227 100%)",
        color: loading || disabled ? "#666" : "#171829",
        boxShadow: loading || disabled ? "none" : "0 4px 24px rgba(201,162,39,0.25)",
      }}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <span className="inline-block w-4 h-4 border-2 border-[#555] border-t-[#c9a227] rounded-full animate-spin" />
          {loadingLabel}
        </span>
      ) : (
        label
      )}
    </button>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="bg-[#1f2135] border border-[#373954] rounded-xl p-4 space-y-1">
      <div className="text-[10px] font-semibold tracking-[.2em] uppercase text-[#a2a2c0]">{label}</div>
      <div className={`text-xl font-bold ${accent ? "text-[#c9a227]" : "text-[#f5f4ff]"}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#787898]">{sub}</div>}
    </div>
  );
}

function ResultBox({ result, title }) {
  if (!result) return null;
  return (
    <div className="mt-6 rounded-xl border border-[#373954] bg-[#0e0f18] overflow-hidden animate-fadeIn">
      {title && (
        <div className="px-5 py-3 border-b border-[#373954] bg-[#1e1f30]">
          <span className="text-xs font-bold tracking-[.15em] uppercase text-[#c9a227]">{title}</span>
        </div>
      )}
      <div className="p-5 text-sm leading-relaxed text-[#e4e2f0] whitespace-pre-wrap">{result}</div>
    </div>
  );
}

function Section({ title, children, noPad }) {
  return (
    <div className="bg-[#252737] border border-[#373954] rounded-2xl overflow-hidden">
      {title && (
        <div className="px-6 py-4 border-b border-[#373954]">
          <h3 className="text-sm font-bold tracking-[.12em] uppercase text-[#c4c4da]">{title}</h3>
        </div>
      )}
      <div className={noPad ? "" : "p-6"}>{children}</div>
    </div>
  );
}

// ─── Postcode Address Lookup ───
function PostcodeLookup({ postcode, onPostcodeChange, onSelect }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const lookup = async () => {
    const pc = postcode.trim();
    if (!pc) return;
    setLoading(true);
    setError("");
    setInfo("");
    setSuggestions([]);
    setOpen(false);

    // Step 1: getaddress.io via server-side proxy (avoids CORS, injects API key)
    try {
      const res = await fetch(`/api/ga/autocomplete/${encodeURIComponent(pc)}?all=true`);
      if (res.ok) {
        const data = await res.json();
        const list = data.suggestions || [];
        if (list.length > 0) {
          setSuggestions(list.map(s => ({ display: s.address, id: s.id })));
          setOpen(true);
          setLoading(false);
          return;
        }
      }
    } catch { /* fall through to postcodes.io */ }

    // Fallback: postcodes.io — fills city/county only
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc.replace(/\s/g, ""))}`);
      if (!res.ok) { setError("Postcode not found — check it's correct."); return; }
      const { result } = await res.json();
      onSelect({ line1: "", line2: "", city: result.admin_district || result.parish || "", county: result.admin_county || result.region || "" });
      setInfo("City/county filled — enter street address manually.");
    } catch {
      setError("Lookup failed. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const select = async (item) => {
    setOpen(false);
    setSuggestions([]);
    if (item.id) {
      setLoading(true);
      try {
        const res = await fetch(`/api/ga/get/${item.id}`);
        if (res.ok) {
          const addr = await res.json();
          onSelect({ line1: addr.line_1 || "", line2: addr.line_2 || "", city: addr.town_or_city || "", county: addr.county || "" });
          return;
        }
      } catch { /* fall through */ } finally { setLoading(false); }
    }
    onSelect(item);
  };

  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold tracking-[.15em] uppercase text-[#b8b8d0]">Postcode</label>
      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 bg-[#1f2135] border border-[#3e4060] rounded-lg px-4 py-3 text-[#f5f4ff] placeholder-[#444] text-sm focus:outline-none focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a22744] transition-all"
          value={postcode}
          onChange={(e) => { onPostcodeChange(e.target.value.toUpperCase()); setError(""); setInfo(""); setOpen(false); }}
          placeholder="B1 1AA"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
        />
        <button
          type="button"
          onClick={lookup}
          disabled={loading || !postcode.trim()}
          className="px-3 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#c9a227] border border-[#c9a22744] hover:bg-[#c9a22711] transition-colors disabled:opacity-40 whitespace-nowrap"
        >
          {loading ? "…" : "Find"}
        </button>
      </div>
      {error && <p className="text-[11px] text-[#f87171]">{error}</p>}
      {!error && info && <p className="text-[11px] text-[#4ade80]">{info}</p>}
      {open && suggestions.length > 0 && (
        <div className="relative">
          <div className="absolute z-50 w-full bg-[#26273a] border border-[#3e4060] rounded-lg mt-1 max-h-52 overflow-y-auto shadow-xl">
            <div className="px-3 py-2 border-b border-[#3e4060] flex items-center justify-between">
              <span className="text-[10px] text-[#a2a2c0] uppercase tracking-wider">{suggestions.length} address{suggestions.length !== 1 ? "es" : ""} found</span>
              <button type="button" onClick={() => setOpen(false)} className="text-[#62647c] hover:text-[#f87171] text-xs">✕</button>
            </div>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => select(s)}
                className="w-full text-left px-3 py-2.5 text-sm text-[#f5f4ff] hover:bg-[#1f2135] transition-colors border-b border-[#2e3060] last:border-0"
              >
                {s.display || s.line1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Deal Activity Modal ───
function DealActivityModal({ dealId, onSave, onClose }) {
  const [f, setF] = useState({ interaction_type: "call", subject: "", body: "", outcome: "neutral", follow_up_date: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true); setError("");
    const result = await onSave(f);
    if (result === null) { setError("Failed to save. Check your connection or Supabase RLS policies."); setSaving(false); }
    // onSave closes the modal on success
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">Log Activity</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <Select label="Type" value={f.interaction_type} onChange={set("interaction_type")} options={["call","email","sms","viewing","meeting","offer","survey","legal","note"]} />
        <Field label="Subject" value={f.subject} onChange={set("subject")} placeholder="e.g. Viewing booked" />
        <Field label="Notes" value={f.body} onChange={set("body")} placeholder="What happened…" textarea />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Outcome" value={f.outcome} onChange={set("outcome")} options={["positive","neutral","negative","no_answer","callback_requested"]} />
          <Field label="Follow-up Date" value={f.follow_up_date} onChange={set("follow_up_date")} type="date" />
        </div>
        {error && <div className="text-xs text-[#f87171] bg-[#f8717111] border border-[#f8717133] rounded-lg px-3 py-2">{error}</div>}
        <GoldButton label={saving ? "Saving…" : "Log Activity"} onClick={handleSave} />
      </div>
    </div>
  );
}

// ─── Dashboard (Kanban) ───
function KanbanCard({ deal, onRemove, onUpdateStatus, onUpdateNotes, activities, onExpand, onLogActivity, onDeleteActivity }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(deal.notes || "");
  const [showExpenses, setShowExpenses] = useState(false);
  const [showInvestors, setShowInvestors] = useState(false);
  const score = deal.score;
  const scoreColor = score >= 7 ? "#4ade80" : score >= 4 ? "#facc15" : "#f87171";
  const stageIdx = PIPELINE_STAGES.findIndex((s) => s.id === deal.pipelineStatus);
  const nextStage = PIPELINE_STAGES[stageIdx + 1];
  const prevStage = PIPELINE_STAGES[stageIdx - 1];
  const daysIn = Math.floor((Date.now() - new Date(deal.savedAt)) / 86400000);

  const saveNotes = () => {
    onUpdateNotes(deal.id, notes);
  };

  return (
    <div className="bg-[#13142a] border border-[#252545] rounded-xl overflow-hidden hover:border-[#3e4060] transition-all">
      {/* Card header — always visible */}
      <div className="p-4 cursor-pointer" onClick={() => { setExpanded((v) => !v); if (!expanded) onExpand(deal.id); }}>
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black shrink-0"
            style={{ background: scoreColor + "20", color: scoreColor, border: `1px solid ${scoreColor}44` }}
          >
            {score || "—"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#f0eef8] leading-snug">{deal.address || "Untitled Deal"}</div>
            <div className="text-[11px] text-[#6a6a82] mt-0.5">{deal.type}{deal.strategy ? ` · ${deal.strategy}` : ""}</div>
          </div>
          <span className="text-[10px] text-[#404060] shrink-0 mt-0.5">{daysIn}d</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className="text-base font-bold text-[#c9a227]">{fmt(deal.profit)}</div>
            <div className="text-[9px] text-[#505270] uppercase tracking-wider">profit</div>
          </div>
          <div>
            <div className="text-sm font-semibold text-[#b8b8d0]">{fmt(deal.price)}</div>
            <div className="text-[9px] text-[#505270] uppercase tracking-wider">asking</div>
          </div>
          <div>
            <div className="text-sm font-semibold text-[#b8b8d0]">{deal.offerPrice ? fmt(deal.offerPrice) : (deal.yield ? pct(deal.yield) : "—")}</div>
            <div className="text-[9px] text-[#505270] uppercase tracking-wider">{deal.offerPrice ? "offer" : "yield"}</div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#1e1e3a] px-4 pb-4 pt-3 space-y-3">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={saveNotes}
            placeholder="Add notes…"
            className="w-full bg-[#0a0a1a] border border-[#2a2a4a] rounded-lg px-3 py-2 text-xs text-[#e0dfe4] placeholder-[#333355] resize-none focus:outline-none focus:border-[#c9a227] min-h-[64px]"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setShowExpenses(true)}
              className="py-2 rounded-lg text-[10px] font-bold tracking-wide uppercase text-[#fb923c] border border-[#fb923c44] hover:bg-[#fb923c11] transition-colors"
            >
              💰 Costs
            </button>
            <button
              onClick={() => setShowInvestors(true)}
              className="py-2 rounded-lg text-[10px] font-bold tracking-wide uppercase text-[#60a5fa] border border-[#60a5fa44] hover:bg-[#60a5fa11] transition-colors"
            >
              💼 JVs
            </button>
          </div>

          {/* Activity Timeline */}
          <div className="border-t border-[#1e1e3a] pt-3 space-y-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold tracking-[.15em] uppercase text-[#6a6a82]">Activity</span>
              <button
                onClick={(e) => { e.stopPropagation(); onLogActivity(deal.id); }}
                className="text-[10px] font-bold px-2.5 py-1 rounded-lg bg-[#c9a22718] text-[#c9a227] border border-[#c9a22730] hover:bg-[#c9a22728] transition-colors"
              >
                + Log
              </button>
            </div>
            {(!activities || activities.length === 0) && (
              <div className="text-[11px] text-[#404060] text-center py-3">No activity yet</div>
            )}
            <div className="space-y-1.5">
              {(activities || []).map(a => (
                <div key={a.id} className="flex gap-2 items-start group bg-[#0d0d20] rounded-lg px-2.5 py-2">
                  <div className="text-sm shrink-0 mt-0.5">{TYPE_ICONS[a.interaction_type] || "📋"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-[#e0dfe4] truncate">{a.subject || a.interaction_type}</span>
                      <span className="text-[9px] text-[#404060] ml-auto shrink-0">
                        {new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </span>
                    </div>
                    {a.outcome && (
                      <span className="text-[9px] font-bold uppercase"
                        style={{ color: OUTCOME_COLORS[a.outcome] || "#a2a2c0" }}>
                        {a.outcome.replace(/_/g, " ")}
                      </span>
                    )}
                    {a.body && <div className="text-[11px] text-[#606080] mt-0.5 truncate">{a.body}</div>}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteActivity(deal.id, a.id); }}
                    className="text-[#62647c] hover:text-[#f87171] transition-colors text-xs opacity-0 group-hover:opacity-100 shrink-0 mt-0.5"
                  >✕</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showExpenses && <DealExpensesModal deal={deal} onClose={() => setShowExpenses(false)} />}
      {showInvestors && <DealInvestorsModal deal={deal} onClose={() => setShowInvestors(false)} />}

      <div className="flex border-t border-[#2e3044]">
        {prevStage && (
          <button
            onClick={() => onUpdateStatus(deal.id, prevStage.id)}
            className="px-3 py-2 text-[10px] font-bold text-[#505270] hover:text-[#f5f4ff] hover:bg-[#252545] transition-colors border-r border-[#2e3044]"
            title={`Move back to ${prevStage.label}`}
          >
            ◀
          </button>
        )}
        {nextStage && (
          <button
            onClick={() => onUpdateStatus(deal.id, nextStage.id)}
            className="flex-1 py-2 text-[10px] font-bold tracking-wider uppercase hover:bg-[#252545] transition-colors border-r border-[#2e3044]"
            style={{ color: nextStage.color + "cc" }}
          >
            ▶ {nextStage.label}
          </button>
        )}
        <button
          onClick={() => onRemove(deal.id)}
          className="px-3 py-2 text-[#3e4060] hover:text-[#f87171] hover:bg-[#f8717111] transition-colors text-sm"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function Dashboard({ deals, userId, onNavigate, onRemoveDeal, onUpdateStatus, onUpdateNotes }) {
  const activeDeals = deals.filter((d) => d.pipelineStatus !== "archived");
  const [dealActivities, setDealActivities] = useState({});
  const [loadedDeals, setLoadedDeals] = useState(new Set());
  const [showActivityModal, setShowActivityModal] = useState(null);

  const loadDealActivities = async (dealId) => {
    if (loadedDeals.has(dealId)) return;
    const { data } = await supabase.from("interactions").select("*").eq("deal_id", dealId).order("created_at", { ascending: false });
    setDealActivities(p => ({ ...p, [dealId]: data || [] }));
    setLoadedDeals(p => new Set([...p, dealId]));
  };

  const logDealActivity = async (dealId, data) => {
    const payload = { ...data, deal_id: dealId, follow_up_date: data.follow_up_date || null };
    const { data: row, error } = await supabase.from("interactions").insert(payload).select().single();
    if (error) { console.error("logDealActivity:", error.message); return null; }
    if (row) setDealActivities(p => ({ ...p, [dealId]: [row, ...(p[dealId] || [])] }));
    return row;
  };

  const deleteDealActivity = async (dealId, id) => {
    await supabase.from("interactions").delete().eq("id", id);
    setDealActivities(p => ({ ...p, [dealId]: (p[dealId] || []).filter(a => a.id !== id) }));
  };

  const totalProfit = activeDeals.reduce((s, d) => s + (d.profit || 0), 0);
  const activeOffers = activeDeals.filter((d) => d.pipelineStatus === "offer").length;
  const inLegal = activeDeals.filter((d) => d.pipelineStatus === "in_legal").length;
  const exchanged = activeDeals.filter((d) => d.pipelineStatus === "completed").length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Deal Pipeline
        </h2>
        <p className="text-sm text-[#a2a2c0]">Kanban board — track every deal from research to completion</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Deals Tracked" value={activeDeals.length} accent />
        <StatCard label="Total Est. Profit" value={fmt(totalProfit)} />
        <StatCard label="Active Offers" value={activeOffers} sub="Made + Under Offer" />
        <StatCard label="In Legal" value={inLegal} sub="Searches & survey" />
        <StatCard label="Completed" value={exchanged} sub="Exchanged or done" />
      </div>


      <div className="text-center py-1">
        <p style={{ fontFamily: "'Dancing Script', cursive", fontSize: "2.2rem", color: "#c9a227", letterSpacing: "0.01em" }}>
          "Remember boy... Money loves speed, but wealth loves time!"
        </p>
      </div>

      {deals.length === 0 ? (
        <div className="text-center py-12 text-[#62647c]">
          <div className="text-4xl mb-3">🏠</div>
          <div className="text-sm font-semibold">No deals saved yet</div>
          <div className="text-xs text-[#505270] mt-1">Use the Deal Analyzer to evaluate properties and save them here</div>
        </div>
      ) : (
        <div className="border border-[#2a2b45] rounded-2xl bg-[#0b0c1a] p-4 overflow-x-auto">
          <div className="flex gap-4 min-w-max">
            {PIPELINE_STAGES.map((stage) => {
              const stageDeals = deals.filter((d) => (d.pipelineStatus || "researching") === stage.id);
              return (
                <div key={stage.id} className="w-72 flex-shrink-0">
                  {/* Column header */}
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: stage.color }} />
                    <span className="text-xs font-bold uppercase tracking-wider flex-1" style={{ color: stage.color }}>
                      {stage.label}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: stage.color + "22", color: stage.color }}>
                      {stageDeals.length}
                    </span>
                  </div>
                  {/* Column body */}
                  <div className="bg-[#0d0d1a] rounded-xl p-2 space-y-2 min-h-[120px]">
                    {stageDeals.map((deal) => (
                      <KanbanCard
                        key={deal.id}
                        deal={deal}
                        onRemove={onRemoveDeal}
                        onUpdateStatus={onUpdateStatus}
                        onUpdateNotes={onUpdateNotes}
                        activities={dealActivities[deal.id]}
                        onExpand={loadDealActivities}
                        onLogActivity={(id) => setShowActivityModal(id)}
                        onDeleteActivity={deleteDealActivity}
                      />
                    ))}
                    {stageDeals.length === 0 && (
                      <div className="border border-dashed border-[#1e1e3a] rounded-xl p-6 text-center text-[10px] text-[#404060]">
                        Drop deals here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showActivityModal && (
        <DealActivityModal
          dealId={showActivityModal}
          onSave={async (data) => { const row = await logDealActivity(showActivityModal, data); if (row) setShowActivityModal(null); return row; }}
          onClose={() => setShowActivityModal(null)}
        />
      )}
    </div>
  );
}

// ─── Market Scout ───
function MarketScout({ apiKey }) {
  const [location, setLocation] = useState("");
  const [budget, setBudget] = useState("");
  const [propType, setPropType] = useState("Terraced House");
  const [strategy, setStrategy] = useState("Below Market Value");
  const [radius, setRadius] = useState("5");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [hpiData, setHpiData] = useState(null);
  const [hpiLoading, setHpiLoading] = useState(false);
  const [hpiError, setHpiError] = useState("");

  const run = async () => {
    if (!location) return;
    setLoading(true);
    setResult("");
    setHpiData(null);
    setHpiError("");

    // Fetch verified HPI data first
    setHpiLoading(true);
    let localHpi = null;
    try {
      localHpi = await fetchHPI(location);
      setHpiData(localHpi);
    } catch (e) {
      setHpiError(e.message);
    }
    setHpiLoading(false);

    const hpiContext = localHpi
      ? `\n\nVERIFIED UK HPI DATA for ${localHpi.region} (${localHpi.month}):\n— Average Price: ${fmt(localHpi.averagePrice)}\n— 12-month change: ${localHpi.annualChange != null ? localHpi.annualChange.toFixed(1) + "%" : "N/A"}\n— Terraced: ${fmt(localHpi.terraced)} | Semi-Det: ${fmt(localHpi.semiDetached)} | Detached: ${fmt(localHpi.detached)} | Flat: ${fmt(localHpi.flat)}\n— Sales Volume: ${localHpi.salesVolume || "N/A"}\n\nUse these verified HMLR figures in your analysis — do not contradict them.`
      : "";

    try {
      const sys = `You are an expert UK property investment analyst specialising in finding below-market-value (BMV) properties. You have deep knowledge of every UK region, town, and neighbourhood. Provide specific, actionable intelligence with real street names, postcodes, and local market data. Always use £. Be data-driven and brutally practical. Structure responses with clear headers using ALL CAPS.`;
      const msg = `I'm searching for below-market-value ${propType} properties in ${location}, UK${budget ? ` with a maximum budget of ${budget}` : ""}${radius ? ` within a ${radius}-mile radius` : ""}. My investment strategy is ${strategy}.${hpiContext}

Provide a comprehensive market intelligence report:

1. MARKET SNAPSHOT
— Current median house prices for the area (use verified HPI data above if provided, be specific to property type)
— Average price per sq ft
— Average days on market and current inventory levels
— 12-month price trend (rising/falling/flat) and percentage (use verified HPI data above if provided)
— Rental yields for this property type in this area

2. BMV HOTSPOTS (name at least 5)
— Specific streets, roads, estates or postcodes where distressed/undervalued properties are most commonly found
— WHY each is a hotspot (council estates being improved, ex-local authority, regeneration zones, etc.)
— Typical discount vs market value you'd expect in each

3. SOURCING CHANNELS (Top 10 for this exact area)
— Name specific local auction houses (e.g., SDL, Allsop, local firms)
— Specific estate agents known for handling BMV/probate/repossession stock
— Local council housing teams and contacts
— Specific websites and portals (Rightmove filters, EIG, auction sites)
— Networking opportunities (local REIA groups, property meetups, landlord associations)
— Direct-to-vendor strategies specific to this market
— Solicitor and probate sourcing angles
— Repossession specialists in the area
— Lease-related opportunities (short lease flats if applicable)
— Other creative sourcing methods for this location

4. UPCOMING OPPORTUNITIES
— Any known regeneration projects, new transport links, HS2/Crossrail effects
— Planning changes that could create value
— Areas tipped for price growth

5. RED FLAGS
— Common pitfalls specific to this market (flood risk, subsidence, mining, Japanese knotweed areas, etc.)
— Areas or streets to avoid and why
— Legal or leasehold traps

6. 7-DAY ACTION PLAN
— Specific, concrete actions to take each day this week to start finding BMV deals in this exact area`;
      const r = await callClaude(apiKey, sys, msg);
      setResult(r);
    } catch (e) {
      setResult("⚠️ " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Market Scout
        </h2>
        <p className="text-sm text-[#a2a2c0]">AI-powered intelligence grounded in verified HMLR HPI data</p>
      </div>
      <Section>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Target Location" value={location} onChange={setLocation} placeholder="e.g. Headington, Oxford or LS8, Leeds" />
            <Field label="Budget" value={budget} onChange={setBudget} placeholder="e.g. 300000" prefix="£" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select label="Property Type" value={propType} onChange={setPropType} options={PROPERTY_TYPES} />
            <Select label="Strategy" value={strategy} onChange={setStrategy} options={STRATEGIES} />
            <Field label="Search Radius (miles)" value={radius} onChange={setRadius} placeholder="5" type="number" />
          </div>
          <GoldButton onClick={run} loading={loading} label="Scout This Market" />
        </div>
      </Section>

      {/* HPI Data Panel */}
      {hpiLoading && (
        <div className="text-xs text-[#a2a2c0] px-1">Loading HMLR HPI data…</div>
      )}
      {hpiError && !hpiData && (
        <div className="text-xs text-[#787898] px-1">HPI data unavailable for this area — AI will use estimated figures</div>
      )}
      {hpiData && (
        <Section title={`Verified HMLR HPI Data — ${hpiData.region} (${hpiData.month})`}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Average Price" value={fmt(hpiData.averagePrice)} accent />
              <StatCard
                label="12-Month Change"
                value={hpiData.annualChange != null ? pct(hpiData.annualChange) : "—"}
                sub={hpiData.annualChange > 0 ? "Rising" : hpiData.annualChange < 0 ? "Falling" : "Flat"}
              />
              <StatCard label="Monthly Change" value={hpiData.monthlyChange != null ? pct(hpiData.monthlyChange) : "—"} />
              <StatCard label="Sales Volume" value={hpiData.salesVolume || "—"} sub="Transactions" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Terraced" value={fmt(hpiData.terraced)} />
              <StatCard label="Semi-Detached" value={fmt(hpiData.semiDetached)} />
              <StatCard label="Detached" value={fmt(hpiData.detached)} />
              <StatCard label="Flat" value={fmt(hpiData.flat)} />
            </div>
            <div className="text-[10px] text-[#50516e]">Source: HM Land Registry UK House Price Index. © Crown copyright.</div>
          </div>
        </Section>
      )}

      <ResultBox result={result} title="Market Intelligence Report" />
    </div>
  );
}

// ─── Deal Analyzer ───
const DISTRESS_STATUSES = ["administration", "liquidation", "receivership", "dissolved"];

function DealAnalyzer({ apiKey, onSaveDeal, epcEmail, epcKey, contacts = [] }) {
  const [address, setAddress] = useState("");
  const [arv, setArv] = useState("");
  const [asking, setAsking] = useState("");
  const [offer, setOffer] = useState("");
  const [repair, setRepair] = useState("");
  const [monthlyRent, setMonthlyRent] = useState("");
  const [propType, setPropType] = useState("Terraced House");
  const [strategy, setStrategy] = useState("Fix & Flip");
  const [holding, setHolding] = useState("6");
  const [details, setDetails] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [epcData, setEpcData] = useState(null);
  const [epcLoading, setEpcLoading] = useState(false);
  const [epcError, setEpcError] = useState("");
  // Flood risk
  const [floodData, setFloodData] = useState(null);
  const [floodLoading, setFloodLoading] = useState(false);
  const [floodError, setFloodError] = useState("");
  // Company check
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState("");
  const [vendorContactId, setVendorContactId] = useState("");

  const lookupEPC = async () => {
    const postcode = extractPostcode(address);
    if (!postcode) return setEpcError("No postcode found in address.");
    if (!epcEmail || !epcKey) return setEpcError("Add EPC credentials in Settings.");
    setEpcLoading(true); setEpcError(""); setEpcData(null);
    try {
      const rows = await fetchEPC(postcode, epcEmail, epcKey);
      setEpcData(rows.length ? rows[0] : null);
      if (!rows.length) setEpcError("No EPC found for this postcode.");
    } catch (e) { setEpcError(e.message); }
    setEpcLoading(false);
  };

  const lookupFlood = async (addr) => {
    const postcode = extractPostcode(addr || address);
    if (!postcode) return;
    setFloodLoading(true); setFloodError(""); setFloodData(null);
    try {
      const data = await fetchFloodRisk(postcode);
      setFloodData(data);
    } catch (e) { setFloodError(e.message); }
    setFloodLoading(false);
  };

  // Auto-fetch flood risk when a valid postcode appears in the address
  useEffect(() => {
    const postcode = extractPostcode(address);
    if (!postcode) return;
    const timer = setTimeout(() => lookupFlood(address), 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const lookupCompany = async () => {
    if (!companyQuery.trim()) return;
    setCompanyLoading(true); setCompanyError(""); setCompanyResults([]);
    try {
      const items = await fetchCompany(companyQuery);
      setCompanyResults(items);
      if (!items.length) setCompanyError("No companies found.");
    } catch (e) { setCompanyError(e.message); }
    setCompanyLoading(false);
  };

  const arvN = parseFloat(arv) || 0;
  const askN = parseFloat(asking) || 0;
  const offerN = parseFloat(offer) || 0;
  const repairN = parseFloat(repair) || 0;
  const purchasePrice = offerN || askN;
  const rentN = parseFloat(monthlyRent) || 0;
  const holdN = parseFloat(holding) || 6;

  const mao = arvN * 0.7 - repairN;
  const sdlt = calcSDLT(purchasePrice, true);
  const legalFees = 1500;
  const agentFees = arvN * 0.015;
  const holdingCosts = (purchasePrice * 0.06 / 12) * holdN;
  const totalCosts = purchasePrice + repairN + sdlt + legalFees + agentFees + holdingCosts;
  const flipProfit = arvN - totalCosts;
  const flipROI = totalCosts > 0 ? (flipProfit / totalCosts) * 100 : 0;
  const grossYield = purchasePrice > 0 ? ((rentN * 12) / purchasePrice) * 100 : 0;
  const annualExpenses = rentN * 12 * 0.30;
  const netYield = purchasePrice > 0 ? (((rentN * 12 - annualExpenses) / purchasePrice) * 100) : 0;
  const discountPct = arvN > 0 ? ((arvN - purchasePrice) / arvN) * 100 : 0;

  const dealScore = useMemo(() => {
    if (!arvN || !purchasePrice) return 0;
    let score = 0;
    if (discountPct >= 25) score += 3; else if (discountPct >= 20) score += 2.5; else if (discountPct >= 15) score += 2; else if (discountPct >= 10) score += 1;
    if (flipROI >= 20) score += 2; else if (flipROI >= 15) score += 1.5; else if (flipROI >= 10) score += 1;
    if (grossYield >= 10) score += 2; else if (grossYield >= 7) score += 1.5; else if (grossYield >= 5) score += 1;
    if (purchasePrice <= mao) score += 2; else if (purchasePrice <= mao * 1.05) score += 1;
    if (flipProfit > 30000) score += 1; else if (flipProfit > 15000) score += 0.5;
    return Math.min(10, Math.round(score * 10) / 10);
  }, [arvN, purchasePrice, discountPct, flipROI, grossYield, mao, flipProfit]);

  const scoreColor = dealScore >= 7 ? "#4ade80" : dealScore >= 4 ? "#facc15" : "#f87171";
  const scoreLabel = dealScore >= 8 ? "Excellent" : dealScore >= 6 ? "Good" : dealScore >= 4 ? "Fair" : dealScore > 0 ? "Weak" : "—";

  const epcRating = epcData?.["current-energy-rating"];
  const isMEES = epcRating && ["F", "G"].includes(epcRating);
  const meesUpgradeCost = epcRating === "G" ? "~£15,000" : "~£8,000";
  const floodZone = floodData?.floodAreas?.length > 0;

  const runAI = async () => {
    if (!arvN || !purchasePrice) return;
    setLoading(true);
    setResult("");
    try {
      const sys = `You are an expert UK property deal analyst. Evaluate BMV deals using the 70% rule, MAO formula, cash-on-cash return, and rental yield analysis. Be brutally honest. Always use £. Factor in UK-specific costs (SDLT, solicitor fees, EPC requirements, etc.).`;
      const epcContext = epcData
        ? `\n— EPC Rating: ${epcRating} → ${epcData["potential-energy-rating"] || "?"} potential, Floor Area: ${epcData["total-floor-area"] || "?"}m²${isMEES ? "\n— ⚠️ MEES ALERT: EPC " + epcRating + " — property cannot legally be let. Forced sale signal. Est. upgrade to E: " + meesUpgradeCost : ""}`
        : "";
      const floodContext = floodData
        ? `\n— Flood Risk: ${floodZone ? `HIGH — ${floodData.floodAreas.length} flood area(s) within 1km. Standard BTL mortgage likely unavailable. Restricted buyer pool.` : "Low — No flood areas detected within 1km."}`
        : "";
      const msg = `Analyse this potential BMV deal in depth:

— Address: ${address || "Not specified"}
— Property Type: ${propType}
— Strategy: ${strategy}
— After Repair Value (ARV): £${arv}
— Asking Price: £${asking}
— My Offer: £${offer || "Not yet decided"}
— Estimated Repair/Refurb Costs: £${repair || "Unknown"}
— Expected Monthly Rent: £${monthlyRent || "Unknown"}
— Holding Period: ${holding} months
— SDLT (calculated): £${sdlt.toLocaleString()}
— 70% Rule MAO (calculated): £${Math.round(mao).toLocaleString()}
— Deal Score (calculated): ${dealScore}/10${epcContext}${floodContext}
${details ? `— Additional Details: ${details}` : ""}

I've already calculated the basic numbers. Now provide deeper analysis:

1. DEAL VERDICT — Is this a BUY, NEGOTIATE HARDER, or WALK AWAY? One-line verdict first.

2. RISK ASSESSMENT — Rate risk 1-10 and explain. What could go wrong? (Over-estimate on ARV, hidden structural issues, market downturn, void periods, bad tenants, planning issues, etc.)

3. REFURB STRATEGY — If repair costs were provided, validate them. If not, estimate based on property type and typical BMV condition. Break down by category (kitchen, bathroom, flooring, electrics, plumbing, decoration, external).

4. EXIT STRATEGY COMPARISON — Compare at least 3 strategies for THIS specific deal:
   a) Fix & Flip: projected profit, timeline, risk
   b) Buy & Hold: rental yield, monthly cashflow, 5-year projection
   c) BRRRR: refinance numbers, money-left-in, infinite return potential

5. NEGOTIATION PLAYBOOK — Specific tactics for this deal. What to offer, how to justify it, leverage points, and a fallback position.

6. DUE DILIGENCE CHECKLIST — The 12 most critical checks for this specific property type and area.

7. FINANCING OPTIONS — How to fund this deal (bridging, BTL mortgage, JV, private finance) with example rates and LTVs.`;
      const r = await callClaude(apiKey, sys, msg);
      setResult(r);
    } catch (e) {
      setResult("⚠️ " + e.message);
    }
    setLoading(false);
  };

  const handleSave = () => {
    onSaveDeal({
      address:         address || "Unnamed Deal",
      price:           askN    || null,
      offerPrice:      offerN  || null,
      arv:             arvN    || null,
      repair:          repairN || null,
      profit:          (arvN || purchasePrice) ? Math.round(flipProfit) : null,
      score:           (arvN && purchasePrice) ? dealScore : null,
      type:            propType,
      strategy,
      yield:           grossYield || null,
      monthlyRent:     rentN || null,
      vendorContactId: vendorContactId || null,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Create and Analyze a New Deal
        </h2>
        <p className="text-sm text-[#a2a2c0]">Run instant financials and AI analysis on any property — then save it as a deal with one click</p>
      </div>

      <Section title="Property Details">
        <div className="space-y-4">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Field label="Property Address" value={address} onChange={setAddress} placeholder="e.g. 14 Park Road, Headington, Oxford OX3 8AA" />
            </div>
            <button
              onClick={lookupEPC}
              disabled={epcLoading || !address}
              className="px-4 py-3 rounded-lg text-xs font-bold tracking-wider uppercase border border-[#3e4060] text-[#b8b8d0] hover:text-[#c9a227] hover:border-[#c9a22744] transition-colors disabled:opacity-40 whitespace-nowrap"
            >
              {epcLoading ? "…" : "EPC"}
            </button>
          </div>
          {epcError && <div className="text-xs text-[#f87171]">{epcError}</div>}
          {epcData && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-3 p-3 rounded-lg bg-[#1f2135] border border-[#373954]">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-[#a2a2c0]">EPC</span>
                  <span className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black text-[#171829]" style={{ background: EPC_COLORS[epcRating] || "#888" }}>
                    {epcRating || "?"}
                  </span>
                  <span className="text-[11px] text-[#b8b8d0]">→</span>
                  <span className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black text-[#171829] opacity-60" style={{ background: EPC_COLORS[epcData["potential-energy-rating"]] || "#888" }}>
                    {epcData["potential-energy-rating"] || "?"}
                  </span>
                  <span className="text-[11px] text-[#a2a2c0]">potential</span>
                </div>
                {epcData["total-floor-area"] && <span className="text-[11px] text-[#b8b8d0]">{epcData["total-floor-area"]} m²</span>}
                {epcData["property-type"] && <span className="text-[11px] text-[#b8b8d0]">{epcData["property-type"]}</span>}
                {epcData["construction-age-band"] && <span className="text-[11px] text-[#b8b8d0]">Built: {epcData["construction-age-band"]}</span>}
                {epcData["inspection-date"] && <span className="text-[11px] text-[#a2a2c0]">Inspected: {epcData["inspection-date"]}</span>}
              </div>
              {isMEES && (
                <div className="p-3 rounded-lg bg-[#f8717122] border border-[#f87171]">
                  <div className="text-xs font-bold text-[#f87171] mb-1">⚠️ MEES ALERT — EPC {epcRating}: Cannot Be Legally Let</div>
                  <div className="text-[11px] text-[#f87171]/90">Under MEES 2025, this property cannot be rented out. The landlord faces enforcement fines and void periods. This is a strong forced sale signal — motivated seller likely. Estimated upgrade cost to reach E rating: {meesUpgradeCost}. Factor this into your offer.</div>
                </div>
              )}
            </div>
          )}
          {/* Flood Risk */}
          {floodLoading && <div className="text-xs text-[#a2a2c0]">Checking flood risk…</div>}
          {floodError && <div className="text-xs text-[#787898]">Flood check: {floodError}</div>}
          {floodData && (
            <div className={`p-3 rounded-lg border text-xs ${floodZone ? "bg-[#f8717122] border-[#f87171]" : "bg-[#4ade8022] border-[#4ade80]"}`}>
              {floodZone ? (
                <div>
                  <div className="font-bold text-[#f87171] mb-1">🌊 FLOOD RISK — {floodData.floodAreas.length} area(s) within 1km</div>
                  <div className="text-[#f87171]/90">HIGH FLOOD RISK — Standard BTL mortgage likely unavailable. Restricted buyer pool severely limits exit options. Verify flood zone classification and check insurance availability before proceeding.</div>
                </div>
              ) : (
                <div className="text-[#4ade80]">✓ No flood areas detected within 1km of this postcode</div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Property Type" value={propType} onChange={setPropType} options={PROPERTY_TYPES} />
            <Select label="Strategy" value={strategy} onChange={setStrategy} options={STRATEGIES} />
          </div>
        </div>
      </Section>

      {/* Companies House Intelligence */}
      <Section title="Seller / Company Intelligence">
        <div className="space-y-3">
          <p className="text-xs text-[#a2a2c0]">If the seller is a company, search Companies House to detect distress signals — administration, liquidation, overdue accounts. Requires CH API key in Settings.</p>
          {contacts.filter(c => !c.archived).length > 0 && (
            <div>
              <label className="block text-[10px] font-semibold tracking-[.15em] uppercase text-[#a2a2c0] mb-1.5">Link Vendor from Contacts</label>
              <select
                value={vendorContactId}
                onChange={e => setVendorContactId(e.target.value)}
                className="w-full bg-[#1f2135] border border-[#3e4060] rounded-lg px-4 py-3 text-[#f5f4ff] text-sm focus:outline-none focus:border-[#c9a227]"
              >
                <option value="">— Select a vendor contact (optional) —</option>
                {contacts.filter(c => !c.archived).map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.company_name ? ` (${c.company_name})` : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Field label="Company Name" value={companyQuery} onChange={setCompanyQuery} placeholder="e.g. ABC Developments Ltd" />
            </div>
            <button
              onClick={lookupCompany}
              disabled={companyLoading || !companyQuery.trim()}
              className="px-4 py-3 rounded-lg text-xs font-bold tracking-wider uppercase border border-[#3e4060] text-[#b8b8d0] hover:text-[#c9a227] hover:border-[#c9a22744] transition-colors disabled:opacity-40 whitespace-nowrap"
            >
              {companyLoading ? "…" : "Search CH"}
            </button>
          </div>
          {companyError && <div className="text-xs text-[#f87171]">{companyError}</div>}
          {companyResults.length > 0 && (
            <div className="space-y-2">
              {companyResults.map((co, i) => {
                const isDistressed = DISTRESS_STATUSES.some((s) => (co.company_status || "").toLowerCase().includes(s));
                return (
                  <div key={i} className={`p-3 rounded-lg border ${isDistressed ? "bg-[#c9a22711] border-[#c9a22744]" : "bg-[#1f2135] border-[#373954]"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#f0eef8] truncate">{co.title || co.company_name}</div>
                        <div className="text-[11px] text-[#a2a2c0]">{co.company_number} · {co.address_snippet || "No address"}</div>
                      </div>
                      <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-1 rounded-full ${isDistressed ? "bg-[#c9a22733] text-[#c9a227]" : "bg-[#4ade8022] text-[#4ade80]"}`}>
                        {co.company_status || "unknown"}
                      </span>
                    </div>
                    {isDistressed && (
                      <div className="mt-2 text-xs text-[#c9a227] font-semibold">⚡ DISTRESS SIGNAL — Contact the insolvency practitioner directly. Property assets may be available before they reach the open market.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Section>

      <Section title="Financials">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="After Repair Value (ARV)" value={arv} onChange={setArv} placeholder="350000" prefix="£" />
            <Field label="Asking Price" value={asking} onChange={setAsking} placeholder="250000" prefix="£" />
            <Field label="Your Offer" value={offer} onChange={setOffer} placeholder="220000" prefix="£" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Repair / Refurb Costs" value={repair} onChange={setRepair} placeholder="40000" prefix="£" />
            <Field label="Expected Monthly Rent" value={monthlyRent} onChange={setMonthlyRent} placeholder="1200" prefix="£" />
            <Field label="Holding Period" value={holding} onChange={setHolding} placeholder="6" suffix="months" type="number" />
          </div>
          <Field label="Additional Details" value={details} onChange={setDetails} placeholder="Property condition, seller motivation, local market knowledge, why you think it's BMV…" textarea />
        </div>
      </Section>

      {/* Instant Results */}
      {(arvN > 0 || purchasePrice > 0) && (
        <Section title="Instant Analysis">
          <div className="space-y-5">
            {/* Deal Score */}
            <div className="flex items-center gap-5">
              <div
                className="w-20 h-20 rounded-2xl flex flex-col items-center justify-center border-2"
                style={{ borderColor: scoreColor, background: scoreColor + "11" }}
              >
                <div className="text-2xl font-black" style={{ color: scoreColor }}>{dealScore || "—"}</div>
                <div className="text-[9px] font-bold tracking-wider uppercase" style={{ color: scoreColor }}>{scoreLabel}</div>
              </div>
              <div className="flex-1 space-y-1.5">
                <div className="text-xs text-[#b8b8d0]">Deal Score is calculated from: discount to ARV, ROI, rental yield, and 70% rule compliance.</div>
                <div className="h-2 rounded-full bg-[#2e3044] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${dealScore * 10}%`, background: scoreColor }} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="70% Rule MAO" value={fmt(mao)} sub={purchasePrice <= mao ? "✓ Under MAO" : "✗ Over MAO"} accent />
              <StatCard label="Discount to ARV" value={pct(discountPct)} sub={discountPct >= 20 ? "Strong BMV" : discountPct >= 15 ? "Decent" : "Thin margin"} />
              <StatCard label="SDLT (Additional)" value={fmt(sdlt)} sub="Stamp Duty Land Tax" />
              <StatCard label="Legal Fees (est.)" value={fmt(legalFees)} sub="Conveyancing" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Flip Profit" value={fmt(flipProfit)} sub={`ROI: ${pct(flipROI)}`} accent={flipProfit > 0} />
              <StatCard label="Total Costs" value={fmt(totalCosts)} sub={`Inc. ${holding}m holding`} />
              <StatCard label="Gross Yield" value={pct(grossYield)} sub={`£${Math.round(rentN * 12).toLocaleString()}/yr`} />
              <StatCard label="Net Yield (est.)" value={pct(netYield)} sub="After 30% expenses" />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <GoldButton onClick={runAI} loading={loading} label="Run AI Deep Analysis" />
              </div>
              <button
                onClick={handleSave}
                className="px-6 rounded-lg border border-[#c9a22744] text-[#c9a227] text-xs font-bold tracking-wider uppercase hover:bg-[#c9a22711] transition-colors"
              >
                Save Deal
              </button>
            </div>
          </div>
        </Section>
      )}

      <ResultBox result={result} title="AI Deep Analysis" />
    </div>
  );
}

// ─── Calculators ───
function Calculators() {
  const [calcTab, setCalcTab] = useState("sdlt");
  const [price, setPrice] = useState("250000");
  const [isAdditional, setIsAdditional] = useState(true);
  const [rent, setRent] = useState("1200");
  const [deposit, setDeposit] = useState("25");
  const [mortgageRate, setMortgageRate] = useState("5.5");
  const [mortgageTerm, setMortgageTerm] = useState("25");
  const [expenses, setExpenses] = useState("30");
  // BRRRR
  const [bPurchase, setBPurchase] = useState("180000");
  const [bRefurb, setBRefurb] = useState("30000");
  const [bArv, setBArv] = useState("280000");
  const [bRefiLtv, setBRefiLtv] = useState("75");
  const [bRate, setBRate] = useState("5.5");
  const [bRent, setBRent] = useState("1400");

  const priceN = parseFloat(price) || 0;
  const rentN = parseFloat(rent) || 0;
  const depPct = parseFloat(deposit) || 25;
  const rateN = parseFloat(mortgageRate) || 5.5;
  const termN = parseFloat(mortgageTerm) || 25;
  const expPct = parseFloat(expenses) || 30;

  // SDLT calc
  const sdlt = calcSDLT(priceN, isAdditional);
  const sdltPct = priceN > 0 ? ((sdlt / priceN) * 100).toFixed(2) : 0;

  // Mortgage calc
  const loanAmount = priceN * (1 - depPct / 100);
  const depositAmount = priceN * (depPct / 100);
  const monthlyRate = rateN / 100 / 12;
  const totalPayments = termN * 12;
  const monthlyMortgage = monthlyRate > 0 ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1) : 0;
  const interestOnly = loanAmount * monthlyRate;

  // Cashflow calc
  const annualRent = rentN * 12;
  const annualExpensesAmt = annualRent * (expPct / 100);
  const monthlyExpenses = annualExpensesAmt / 12;
  const monthlyCashflow = rentN - monthlyExpenses - interestOnly;
  const annualCashflow = monthlyCashflow * 12;
  const cashOnCash = depositAmount > 0 ? (annualCashflow / (depositAmount + sdlt + 1500)) * 100 : 0;
  const grossY = priceN > 0 ? (annualRent / priceN) * 100 : 0;
  const netY = priceN > 0 ? ((annualRent - annualExpensesAmt) / priceN) * 100 : 0;

  // BRRRR calc
  const bp = parseFloat(bPurchase) || 0;
  const br = parseFloat(bRefurb) || 0;
  const ba = parseFloat(bArv) || 0;
  const bltv = parseFloat(bRefiLtv) || 75;
  const bRateN = parseFloat(bRate) || 5.5;
  const bRentN = parseFloat(bRent) || 0;
  const bTotalIn = bp + br + calcSDLT(bp, true) + 1500;
  const bRefiAmount = ba * (bltv / 100);
  const bMoneyLeft = bTotalIn - bRefiAmount;
  const bMonthlyMortgage = bRefiAmount * (bRateN / 100 / 12);
  const bMonthlyCashflow = bRentN - bRentN * 0.30 - bMonthlyMortgage;
  const bROI = bMoneyLeft > 0 ? ((bMonthlyCashflow * 12) / bMoneyLeft) * 100 : Infinity;

  const calcs = [
    { id: "sdlt", label: "SDLT" },
    { id: "yield", label: "Yield & Cashflow" },
    { id: "brrrr", label: "BRRRR" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Calculators
        </h2>
        <p className="text-sm text-[#a2a2c0]">Instant UK property investment calculations — no AI needed</p>
      </div>

      <div className="flex gap-2">
        {calcs.map((c) => (
          <button
            key={c.id}
            onClick={() => setCalcTab(c.id)}
            className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all"
            style={{
              background: calcTab === c.id ? "#c9a22722" : "transparent",
              color: calcTab === c.id ? "#c9a227" : "#a2a2c0",
              border: `1px solid ${calcTab === c.id ? "#c9a22744" : "#373954"}`,
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {calcTab === "sdlt" && (
        <Section title="Stamp Duty Land Tax Calculator">
          <div className="space-y-4">
            <Field label="Purchase Price" value={price} onChange={setPrice} placeholder="250000" prefix="£" />
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsAdditional(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{ background: isAdditional ? "#c9a22722" : "#1f2135", color: isAdditional ? "#c9a227" : "#a2a2c0", border: `1px solid ${isAdditional ? "#c9a22744" : "#3e4060"}` }}
              >
                <span className={`w-3 h-3 rounded-full border-2 ${isAdditional ? "bg-[#c9a227] border-[#c9a227]" : "border-[#444]"}`} />
                Additional Property
              </button>
              <button
                onClick={() => setIsAdditional(false)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{ background: !isAdditional ? "#c9a22722" : "#1f2135", color: !isAdditional ? "#c9a227" : "#a2a2c0", border: `1px solid ${!isAdditional ? "#c9a22744" : "#3e4060"}` }}
              >
                <span className={`w-3 h-3 rounded-full border-2 ${!isAdditional ? "bg-[#c9a227] border-[#c9a227]" : "border-[#444]"}`} />
                Main Residence
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              <StatCard label="SDLT Payable" value={fmt(sdlt)} accent />
              <StatCard label="Effective Rate" value={sdltPct + "%"} />
              <StatCard label="Total Purchase Cost" value={fmt(priceN + sdlt + 1500)} sub="Inc. est. legal fees" />
            </div>
            <div className="text-[10px] text-[#606278] mt-2">Based on current England/NI SDLT rates. Scotland (LBTT) and Wales (LTT) rates differ. Verify at gov.uk before relying on these figures.</div>
          </div>
        </Section>
      )}

      {calcTab === "yield" && (
        <Section title="Rental Yield & Cashflow Calculator">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Purchase Price" value={price} onChange={setPrice} placeholder="250000" prefix="£" />
              <Field label="Monthly Rent" value={rent} onChange={setRent} placeholder="1200" prefix="£" />
              <Field label="Deposit" value={deposit} onChange={setDeposit} placeholder="25" suffix="%" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Mortgage Rate" value={mortgageRate} onChange={setMortgageRate} placeholder="5.5" suffix="%" />
              <Field label="Mortgage Term" value={mortgageTerm} onChange={setMortgageTerm} placeholder="25" suffix="years" />
              <Field label="Expense Ratio" value={expenses} onChange={setExpenses} placeholder="30" suffix="%" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
              <StatCard label="Gross Yield" value={pct(grossY)} accent />
              <StatCard label="Net Yield" value={pct(netY)} />
              <StatCard label="Monthly Cashflow" value={fmt(monthlyCashflow)} sub={monthlyCashflow > 0 ? "Positive" : "Negative"} />
              <StatCard label="Cash-on-Cash" value={pct(cashOnCash)} sub="Annual return on cash in" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Deposit Required" value={fmt(depositAmount)} />
              <StatCard label="Loan Amount" value={fmt(loanAmount)} />
              <StatCard label="Monthly Mortgage" value={fmt(Math.round(monthlyMortgage))} sub="Repayment" />
              <StatCard label="Interest Only" value={fmt(Math.round(interestOnly))} sub="Monthly" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <StatCard label="Annual Rent" value={fmt(annualRent)} />
              <StatCard label="Annual Expenses" value={fmt(Math.round(annualExpensesAmt))} sub={`${expPct}% of rent`} />
              <StatCard label="Annual Cashflow" value={fmt(Math.round(annualCashflow))} sub="After mortgage & expenses" />
            </div>
          </div>
        </Section>
      )}

      {calcTab === "brrrr" && (
        <Section title="BRRRR Strategy Calculator">
          <div className="space-y-4">
            <p className="text-xs text-[#a2a2c0]">Buy, Refurb, Rent, Refinance, Repeat — calculate how much money you leave in and your return on capital.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Purchase Price" value={bPurchase} onChange={setBPurchase} placeholder="180000" prefix="£" />
              <Field label="Refurb Costs" value={bRefurb} onChange={setBRefurb} placeholder="30000" prefix="£" />
              <Field label="After Refurb Value" value={bArv} onChange={setBArv} placeholder="280000" prefix="£" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Refinance LTV" value={bRefiLtv} onChange={setBRefiLtv} placeholder="75" suffix="%" />
              <Field label="BTL Mortgage Rate" value={bRate} onChange={setBRate} placeholder="5.5" suffix="%" />
              <Field label="Monthly Rent" value={bRent} onChange={setBRent} placeholder="1400" prefix="£" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
              <StatCard label="Total Cash In" value={fmt(bTotalIn)} sub="Purchase + refurb + costs" />
              <StatCard label="Refinance Amount" value={fmt(Math.round(bRefiAmount))} sub={`${bltv}% of ${fmt(ba)}`} />
              <StatCard label="Money Left In" value={fmt(Math.round(Math.max(0, bMoneyLeft)))} sub={bMoneyLeft <= 0 ? "All money out!" : ""} accent />
              <StatCard label="Cash Recycled" value={fmt(Math.round(Math.min(bRefiAmount, bTotalIn)))} sub="Back in your pocket" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <StatCard label="Monthly Cashflow" value={fmt(Math.round(bMonthlyCashflow))} sub="After mortgage & 30% expenses" />
              <StatCard label="Return on Capital" value={bMoneyLeft > 0 ? pct(bROI) : "∞"} sub={bMoneyLeft <= 0 ? "Infinite — no money left in!" : "Annual"} accent />
              <StatCard label="BTL Mortgage" value={fmt(Math.round(bMonthlyMortgage))} sub="Monthly (interest only)" />
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}

// ─── Property Evaluator ───
function PropertyEvaluator({ apiKey }) {
  const [desc, setDesc] = useState("");
  const [url, setUrl] = useState("");
  const [price, setPrice] = useState("");
  const [area, setArea] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!desc) return;
    setLoading(true);
    setResult("");
    try {
      const sys = `You are an expert UK property investment evaluator. Given a property listing description, identify BMV signals, assess hidden risks, and provide a strategic recommendation. You know typical prices for every UK area and can spot when something is mispriced. Always use £. Be specific and analytical.`;
      const msg = `Evaluate this UK property listing for below-market-value potential:

${desc}
${url ? `\nListing URL: ${url}` : ""}
${price ? `\nListed Price: £${price}` : ""}
${area ? `\nArea/Postcode: ${area}` : ""}

Provide a comprehensive evaluation:

1. BMV CONFIDENCE SCORE (1-10) — How likely is this genuinely below market value? Score with clear justification.

2. BMV SIGNALS DETECTED — What specific indicators suggest this is (or isn't) below market value? List each signal and rate it:
   — Motivated seller language? (e.g., "must sell", "cash buyers only", "priced for quick sale")
   — Below average price per sq ft for the area?
   — Property condition suggesting forced sale?
   — Probate/estate sale indicators?
   — Repossession indicators?
   — Time on market analysis?
   — Chain-free sale?

3. ESTIMATED TRUE MARKET VALUE — Based on the description, what would this property realistically sell for in good condition? Provide a range.

4. DISCOUNT ANALYSIS — What percentage below market value is this listed at? Is the discount genuine or are there hidden reasons?

5. WHY IT MIGHT BE DISCOUNTED — Possible explanations for the low price (seller motivation, property issues, market conditions, etc.)

6. HIDDEN RISKS TO INVESTIGATE
   — Structural (subsidence, damp, roof, Japanese knotweed)
   — Legal (title issues, boundary disputes, restrictive covenants)
   — Leasehold traps (short lease, high ground rent, marriage value)
   — Planning (flood zone, conservation area, listed building, Article 4)
   — Financial (service charges, sinking fund, EWS1 form for flats)
   — Area (crime rates, school catchment, future developments)

7. DUE DILIGENCE CHECKLIST — The 15 most critical checks before making an offer on THIS specific property.

8. RECOMMENDED OFFER STRATEGY — How much to offer, how to structure it, and negotiation tactics specific to this listing.

9. INVESTMENT POTENTIAL — Best strategy for this property (flip, rent, HMO, SA, develop) with projected returns for each viable option.`;
      const r = await callClaude(apiKey, sys, msg);
      setResult(r);
    } catch (e) {
      setResult("⚠️ " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Property Evaluator
        </h2>
        <p className="text-sm text-[#a2a2c0]">Paste a listing description to get an instant BMV assessment and investment analysis</p>
      </div>
      <Section>
        <div className="space-y-4">
          <Field label="Property Listing Description" value={desc} onChange={setDesc} placeholder="Paste the full listing text from Rightmove, Zoopla, auction catalogue, etc. Include price, location, bedrooms, condition, and any other details…" textarea />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Listed Price" value={price} onChange={setPrice} placeholder="220000" prefix="£" />
            <Field label="Area / Postcode" value={area} onChange={setArea} placeholder="e.g. OX3 or Headington" />
            <Field label="Listing URL (optional)" value={url} onChange={setUrl} placeholder="rightmove.co.uk/…" />
          </div>
          <GoldButton onClick={run} loading={loading} label="Evaluate This Property" />
        </div>
      </Section>
      <ResultBox result={result} title="BMV Evaluation Report" />
    </div>
  );
}

// ─── Seller Outreach ───
function SellerOutreach({ apiKey }) {
  const [scenario, setScenario] = useState("Probate / Inherited Property");
  const [medium, setMedium] = useState("Letter");
  const [tone, setTone] = useState("Empathetic & Professional");
  const [addr, setAddr] = useState("");
  const [sellerInfo, setSellerInfo] = useState("");
  const [yourName, setYourName] = useState("");
  const [yourCompany, setYourCompany] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const scenarios = [
    "Probate / Inherited Property", "Pre-Repossession", "Absentee Owner", "Vacant Property",
    "Tired Landlord", "Divorce", "Tax Arrears", "Expired Listing", "Auction Withdrawn",
    "Downsizing / Relocating", "Long Time on Market", "Negative Equity",
  ];
  const mediums = ["Letter", "Postcard", "Cold Call Script", "Door Knock Script", "Text / SMS", "Email", "LinkedIn Message"];

  const run = async () => {
    setLoading(true);
    setResult("");
    try {
      const sys = `You are an expert UK direct-to-vendor property copywriter. Write compelling, ethical, and legally compliant outreach that converts motivated sellers. Adapt to UK conventions (sellers not owners, solicitors not attorneys, etc.). Never be pushy or predatory. Focus on solving the seller's problem. Make every word count.`;
      const msg = `Write a ${medium.toLowerCase()} for this scenario:

— Seller Situation: ${scenario}
— Property: ${addr || "Generic — no specific address"}
— Known Seller Info: ${sellerInfo || "None — keep it general"}
— Tone: ${tone}
— My Name: ${yourName || "Not specified"}
— My Company: ${yourCompany || "Not specified"}

Provide:

1. THE COMPLETE ${medium.toUpperCase()} — Ready to send, formatted appropriately for the medium. If it's a letter, include full layout with addresses. If it's a script, include openers, responses to objections, and closing lines.

2. SUBJECT LINE / HEADLINE — If applicable (email, letter headline)

3. WHY THIS WORKS — The psychology behind this approach for this specific seller type. What emotional triggers are being activated ethically.

4. PERSONALISATION TIPS — How to customise this further if more info becomes available about the seller.

5. FOLLOW-UP SEQUENCE — A 5-touch follow-up plan with:
   — Timing (days between each touchpoint)
   — Medium for each touch (mix of letter, call, visit, etc.)
   — Key message evolution across the sequence
   — When to escalate and when to back off

6. OBJECTION HANDLERS — The 5 most common objections from ${scenario} sellers and exactly how to respond.

7. COMPLIANCE NOTES — UK-specific legal considerations (GDPR, data protection, estate agent regulations, etc.)

8. DO'S AND DON'TS — Critical mistakes to avoid with this seller type.`;
      const r = await callClaude(apiKey, sys, msg);
      setResult(r);
    } catch (e) {
      setResult("⚠️ " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Seller Outreach
        </h2>
        <p className="text-sm text-[#a2a2c0]">Generate tailored outreach for motivated sellers — letters, scripts, and follow-up sequences</p>
      </div>
      <Section>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select label="Seller Scenario" value={scenario} onChange={setScenario} options={scenarios} />
            <Select label="Medium" value={medium} onChange={setMedium} options={mediums} />
            <Select
              label="Tone"
              value={tone}
              onChange={setTone}
              options={["Empathetic & Professional", "Casual & Friendly", "Urgent & Direct", "Corporate / Formal"]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Your Name" value={yourName} onChange={setYourName} placeholder="e.g. Konstantinos" />
            <Field label="Your Company (optional)" value={yourCompany} onChange={setYourCompany} placeholder="e.g. KM Property Investments" />
          </div>
          <Field label="Property Address (optional)" value={addr} onChange={setAddr} placeholder="e.g. 14 Park Road, Oxford OX3 8AA" />
          <Field label="Known Seller Info (optional)" value={sellerInfo} onChange={setSellerInfo} placeholder="e.g. Inherited 6 months ago, lives abroad, property has been empty for 2 years, needs work…" textarea />
          <GoldButton onClick={run} loading={loading} label="Generate Outreach" />
        </div>
      </Section>
      <ResultBox result={result} title="Outreach Copy" />
    </div>
  );
}

// ─── Comparables ───
function Comparables({ apiKey }) {
  const [address, setAddress] = useState("");
  const [propType, setPropType] = useState("Terraced House");
  const [beds, setBeds] = useState("3");
  const [condition, setCondition] = useState("Needs refurbishment");
  const [targetPrice, setTargetPrice] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [soldData, setSoldData] = useState([]);
  const [soldError, setSoldError] = useState("");

  const getLRLabel = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) {
      const first = val[0];
      if (!first) return "";
      if (typeof first === "string") return first;
      return first["@value"] || first.prefLabel || first.label || "";
    }
    const label = val.prefLabel || val.label;
    if (Array.isArray(label)) return getLRLabel(label);
    if (label && typeof label === "object") return label["@value"] || "";
    return label || val["@value"] || "";
  };

  const run = async () => {
    if (!address) return;
    setLoading(true);
    setResult("");
    setSoldData([]);
    setSoldError("");

    let realSalesText = "";
    const postcode = extractPostcode(address);
    if (postcode) {
      try {
        const items = await fetchSoldPrices(postcode);
        setSoldData(items);
        if (items.length) {
          realSalesText = `\n\nREAL LAND REGISTRY SOLD PRICES FOR ${postcode} (most recent first):\n` +
            items.map((it) => {
              const addr = it.propertyAddress;
              const street = addr ? `${addr.paon || ""} ${addr.street || ""}`.trim() : "";
              const type = getLRLabel(it.propertyType);
              const tenure = getLRLabel(it.estateType);
              return `  £${Number(it.pricePaid).toLocaleString("en-GB")} — ${street}, ${postcode} | ${type} | ${tenure} | ${it.transactionDate?.slice(0, 10) || ""}`;
            }).join("\n");
        }
      } catch (e) { setSoldError(e.message); }
    }

    try {
      const sys = `You are an expert UK property valuer with access to comparable sales data, Land Registry records, and local market intelligence. Provide detailed comparable analysis to support accurate valuations. Always use £. Be specific with addresses, dates, and prices where possible.`;
      const msg = `I need a detailed comparable analysis for:

— Property: ${address}
— Type: ${propType}
— Bedrooms: ${beds}
— Condition: ${condition}
${targetPrice ? `— Price I'm considering: £${targetPrice}` : ""}

Provide a comprehensive comparables report:

1. COMPARABLE SALES (find at least 6)
For each comp provide:
   — Full address (be specific — real streets in the area)
   — Sale price and date (use realistic recent data for this area)
   — Property type, bedrooms, and approximate sq ft
   — Condition at sale
   — Price per sq ft
   — How comparable it is to the subject (1-10 match score)
   — Key differences and adjustments needed

2. VALUATION ANALYSIS
   — Adjusted value range based on comps
   — Price per sq ft analysis
   — Condition-adjusted value (current state vs done-up)
   — Confidence level in valuation (Low / Medium / High)

3. AFTER REFURB VALUE (ARV) ESTIMATE
   — What similar properties sell for in good/refurbished condition
   — Specific recent examples
   — Realistic ARV range

4. RENTAL COMPARABLES
   — What similar properties rent for in the area
   — Furnished vs unfurnished rates
   — HMO potential (if applicable) — room rates in the area

5. MARKET CONTEXT
   — Is the area rising, falling, or flat?
   — Average days on market
   — Supply vs demand dynamics
   — How competitive is this postcode for investors?

6. VALUATION VERDICT
   — Is £${targetPrice || "[the asking price]"} fair, high, or low for this property?
   — Recommended maximum purchase price for investment
   — Recommended offer price and negotiation range${realSalesText}`;
      const r = await callClaude(apiKey, sys, msg);
      setResult(r);
    } catch (e) {
      setResult("⚠️ " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Comparables Finder
        </h2>
        <p className="text-sm text-[#a2a2c0]">AI-generated comparable sales analysis to validate your valuations</p>
      </div>
      <Section>
        <div className="space-y-4">
          <Field label="Property Address or Area" value={address} onChange={setAddress} placeholder="e.g. Park Road, Headington, Oxford OX3" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select label="Property Type" value={propType} onChange={setPropType} options={PROPERTY_TYPES} />
            <Field label="Bedrooms" value={beds} onChange={setBeds} type="number" />
            <Select label="Condition" value={condition} onChange={setCondition} options={["Excellent / Refurbished", "Good / Habitable", "Needs updating", "Needs refurbishment", "Derelict / Uninhabitable"]} />
          </div>
          <Field label="Target Price (optional)" value={targetPrice} onChange={setTargetPrice} placeholder="220000" prefix="£" />
          <GoldButton onClick={run} loading={loading} label="Find Comparables" />
        </div>
      </Section>

      {soldError && <div className="text-xs text-[#f87171] px-1">{soldError}</div>}
      {soldData.length > 0 && (
        <Section title={`Land Registry — ${soldData.length} Real Sales Found`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#a2a2c0] text-left border-b border-[#373954]">
                  <th className="pb-2 pr-4 font-semibold uppercase tracking-wider">Address</th>
                  <th className="pb-2 pr-4 font-semibold uppercase tracking-wider">Price</th>
                  <th className="pb-2 pr-4 font-semibold uppercase tracking-wider">Date</th>
                  <th className="pb-2 pr-4 font-semibold uppercase tracking-wider">Type</th>
                  <th className="pb-2 font-semibold uppercase tracking-wider">Tenure</th>
                </tr>
              </thead>
              <tbody>
                {soldData.map((it, i) => {
                  const addr = it.propertyAddress;
                  const street = addr ? `${addr.paon || ""} ${addr.street || ""}`.trim() : "—";
                  const type = getLRLabel(it.propertyType) || "—";
                  const tenure = getLRLabel(it.estateType) || "—";
                  return (
                    <tr key={i} className="border-b border-[#1f2135] hover:bg-[#1f2135] transition-colors">
                      <td className="py-2.5 pr-4 text-[#e8e6f4]">{street}</td>
                      <td className="py-2.5 pr-4 font-bold text-[#c9a227]">£{Number(it.pricePaid).toLocaleString("en-GB")}</td>
                      <td className="py-2.5 pr-4 text-[#b8b8d0]">{it.transactionDate?.slice(0, 10) || "—"}</td>
                      <td className="py-2.5 pr-4 text-[#b8b8d0]">{type}</td>
                      <td className="py-2.5 text-[#b8b8d0]">{tenure}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-[#50516e] mt-3">Source: HM Land Registry Price Paid Data. © Crown copyright 2024.</p>
          </div>
        </Section>
      )}

      <ResultBox result={result} title="Comparable Analysis Report" />
    </div>
  );
}

// ─── Deal Comparables Table ───
function DealComparables({ deals, userId }) {
  const [selectedDealId, setSelectedDealId] = useState("");
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ address: "", sale_price: "", sale_date: "", property_type: "", beds: "", sqft: "", condition: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const sf = (k) => (v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!selectedDealId) { setComps([]); return; }
    setLoading(true);
    supabase.from("deal_comparables").select("*").eq("deal_id", selectedDealId).order("sale_date", { ascending: false })
      .then(({ data }) => { setComps(data || []); setLoading(false); });
  }, [selectedDealId]);

  const addComp = async () => {
    if (!selectedDealId || !form.address || !form.sale_price) return;
    setSaving(true);
    const payload = {
      deal_id: selectedDealId,
      user_id: userId,
      address: form.address,
      sale_price: parseFloat(form.sale_price),
      sale_date: form.sale_date || null,
      property_type: form.property_type || null,
      beds: form.beds ? parseInt(form.beds) : null,
      sqft: form.sqft ? parseFloat(form.sqft) : null,
      condition: form.condition || null,
      notes: form.notes || null,
    };
    const { data } = await supabase.from("deal_comparables").insert(payload).select().single();
    if (data) setComps(p => [data, ...p]);
    setForm({ address: "", sale_price: "", sale_date: "", property_type: "", beds: "", sqft: "", condition: "", notes: "" });
    setSaving(false);
  };

  const removeComp = async (id) => {
    await supabase.from("deal_comparables").delete().eq("id", id);
    setComps(p => p.filter(c => c.id !== id));
  };

  const avgPrice = comps.length ? comps.reduce((s, c) => s + Number(c.sale_price), 0) / comps.length : null;
  const compsWithSqft = comps.filter(c => c.sqft && c.sqft > 0);
  const avgPpsf = compsWithSqft.length ? compsWithSqft.reduce((s, c) => s + Number(c.sale_price) / Number(c.sqft), 0) / compsWithSqft.length : null;

  const dealOptions = deals.map(d => ({ value: d.id, label: d.address }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Comparables</h2>
        <p className="text-sm text-[#a2a2c0]">Record and track comparable sold properties against a specific deal</p>
      </div>

      <Section>
        <Select label="Deal" value={selectedDealId} onChange={setSelectedDealId} options={[{ value: "", label: "— Select a deal —" }, ...dealOptions]} />
      </Section>

      {selectedDealId && (<>
        <Section title="Add Comparable">
          <div className="space-y-4">
            <Field label="Address *" value={form.address} onChange={sf("address")} placeholder="e.g. 22 Park Road, Oxford OX3 8AA" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Sale Price *" value={form.sale_price} onChange={sf("sale_price")} placeholder="220000" prefix="£" type="number" />
              <Field label="Sale Date" value={form.sale_date} onChange={sf("sale_date")} type="date" />
              <Field label="Bedrooms" value={form.beds} onChange={sf("beds")} placeholder="3" type="number" />
              <Field label="Sq Ft" value={form.sqft} onChange={sf("sqft")} placeholder="850" type="number" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select label="Property Type" value={form.property_type} onChange={sf("property_type")} options={["", ...PROPERTY_TYPES]} />
              <Select label="Condition" value={form.condition} onChange={sf("condition")} options={["", "Excellent / Refurbished", "Good / Habitable", "Needs updating", "Needs refurbishment", "Derelict / Uninhabitable"]} />
            </div>
            <Field label="Notes (optional)" value={form.notes} onChange={sf("notes")} placeholder="Chain-free, motivated seller, corner plot…" textarea />
            <GoldButton onClick={addComp} loading={saving} loadingLabel="Saving…" label="Add Comparable" disabled={!form.address || !form.sale_price} />
          </div>
        </Section>

        {loading && <div className="text-sm text-[#a2a2c0] px-1">Loading…</div>}

        {!loading && comps.length > 0 && (<>
          {(avgPrice || avgPpsf) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <StatCard label="Comps" value={comps.length} />
              {avgPrice && <StatCard label="Avg Sale Price" value={fmt(Math.round(avgPrice))} accent />}
              {avgPpsf && <StatCard label="Avg £/sq ft" value={`£${Math.round(avgPpsf)}`} />}
            </div>
          )}
          <Section title={`${comps.length} Comparable${comps.length !== 1 ? "s" : ""}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#a2a2c0] text-left border-b border-[#373954]">
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">Address</th>
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">Price</th>
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">Date</th>
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">Type</th>
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">Beds</th>
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">Sq Ft</th>
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">£/sqft</th>
                    <th className="pb-2 pr-3 font-semibold uppercase tracking-wider">Condition</th>
                    <th className="pb-2 font-semibold uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody>
                  {comps.map(c => (
                    <tr key={c.id} className="border-b border-[#1f2135] hover:bg-[#1f2135] transition-colors group">
                      <td className="py-2.5 pr-3 text-[#e8e6f4] max-w-[160px] truncate">{c.address}</td>
                      <td className="py-2.5 pr-3 font-bold text-[#c9a227]">{fmt(c.sale_price)}</td>
                      <td className="py-2.5 pr-3 text-[#b8b8d0]">{c.sale_date || "—"}</td>
                      <td className="py-2.5 pr-3 text-[#b8b8d0]">{c.property_type || "—"}</td>
                      <td className="py-2.5 pr-3 text-[#b8b8d0]">{c.beds ?? "—"}</td>
                      <td className="py-2.5 pr-3 text-[#b8b8d0]">{c.sqft ? Number(c.sqft).toLocaleString("en-GB") : "—"}</td>
                      <td className="py-2.5 pr-3 text-[#b8b8d0]">{c.sqft && c.sqft > 0 ? `£${Math.round(c.sale_price / c.sqft)}` : "—"}</td>
                      <td className="py-2.5 pr-3 text-[#b8b8d0]">{c.condition || "—"}</td>
                      <td className="py-2.5">
                        <button onClick={() => removeComp(c.id)} className="text-[#62647c] hover:text-[#f87171] transition-colors opacity-0 group-hover:opacity-100">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>)}

        {!loading && comps.length === 0 && (
          <div className="text-center py-12 space-y-2">
            <div className="text-3xl">📍</div>
            <div className="text-sm font-semibold text-[#62647c]">No comparables yet</div>
            <div className="text-xs text-[#505270]">Add comparable sold properties above to build your evidence base</div>
          </div>
        )}
      </>)}
    </div>
  );
}

// ─── Deal Edit Modal ───
function DealEditModal({ deal, onSave, onClose, contacts = [], properties = [], onCreateProperty }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  const [form, setForm] = useState({
    address:          deal.address || "",
    price:            deal.price ?? "",
    offerPrice:       deal.offerPrice ?? "",
    arv:              deal.arv ?? "",
    repair:           deal.repair ?? "",
    monthlyRent:      deal.monthlyRent ?? "",
    strategy:         deal.strategy || "",
    type:             deal.type || "",
    pipelineStatus:   deal.pipelineStatus || "researching",
    notes:            deal.notes || "",
    score:            deal.score ?? "",
    propertyId:       deal.propertyId || "",
    vendorContactId:  deal.vendorContactId || "",
    agentContactId:   deal.agentContactId || "",
    solicitorId:      deal.solicitorId || "",
    brokerContactId:  deal.brokerContactId || "",
  });
  const [showCreateProperty, setShowCreateProperty] = useState(false);
  const sf = (k) => (v) => setForm(p => ({ ...p, [k]: v }));

  const num = (v) => v !== "" ? parseFloat(v) : null;
  const ref = (v) => v || null;

  const handleSave = () => {
    onSave({
      address:          form.address,
      price:            num(form.price),
      offerPrice:       num(form.offerPrice),
      arv:              num(form.arv),
      repair:           num(form.repair),
      monthlyRent:      num(form.monthlyRent),
      strategy:         form.strategy,
      type:             form.type,
      pipelineStatus:   form.pipelineStatus,
      notes:            form.notes,
      score:            num(form.score),
      propertyId:       ref(form.propertyId),
      vendorContactId:  ref(form.vendorContactId),
      agentContactId:   ref(form.agentContactId),
      solicitorId:      ref(form.solicitorId),
      brokerContactId:  ref(form.brokerContactId),
    });
    onClose();
  };

  const SectionHeader = ({ title }) => (
    <div className="border-t border-[#2e3060] pt-4 mt-1">
      <p className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0] mb-3">{title}</p>
    </div>
  );

  const contactOptions = (type) => {
    const filtered = contacts.filter(c => c.contact_type === type);
    const pool = filtered.length ? filtered : contacts;
    return [{ value: "", label: "— None —" }, ...pool.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name}${c.company_name ? " — " + c.company_name : ""}` }))];
  };

  const propertyOptions = [
    { value: "", label: "— None —" },
    ...properties.map(p => ({ value: p.id, label: `${p.address_line_1}${p.city ? ", " + p.city : ""}${p.postcode ? " " + p.postcode : ""}` })),
  ];

  const linkedProperty = properties.find(p => p.id === form.propertyId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#13142a] border border-[#2e3060] rounded-2xl p-6 w-full max-w-xl shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#faf8ff]" style={{ fontFamily: "'Playfair Display', serif" }}>Edit Deal</h3>
          <button onClick={onClose} className="text-[#6a6a82] hover:text-[#f87171] transition-colors text-lg">✕</button>
        </div>

        <Field label="Address" value={form.address} onChange={sf("address")} placeholder="14 Park Road, Oxford OX3" />

        {/* Financials */}
        <SectionHeader title="Financials" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Asking Price" value={form.price} onChange={sf("price")} prefix="£" type="number" placeholder="250000" />
          <Field label="Offer Price" value={form.offerPrice} onChange={sf("offerPrice")} prefix="£" type="number" placeholder="210000" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="ARV" value={form.arv} onChange={sf("arv")} prefix="£" type="number" placeholder="300000" />
          <Field label="Repair Cost" value={form.repair} onChange={sf("repair")} prefix="£" type="number" placeholder="25000" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Monthly Rent" value={form.monthlyRent} onChange={sf("monthlyRent")} prefix="£" type="number" placeholder="1200" />
          <Field label="Deal Score" value={form.score} onChange={sf("score")} type="number" placeholder="1–10" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Strategy" value={form.strategy} onChange={sf("strategy")} options={["", ...STRATEGIES]} />
          <Select label="Pipeline Status" value={form.pipelineStatus} onChange={sf("pipelineStatus")} options={PIPELINE_STAGES.map(s => ({ value: s.id, label: s.label }))} />
        </div>

        {/* Property */}
        <SectionHeader title="Property" />
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Select label="Link Property Record" value={form.propertyId} onChange={sf("propertyId")} options={propertyOptions} />
          </div>
          <button onClick={() => setShowCreateProperty(true)} className="px-3 py-3 rounded-lg text-xs font-bold border border-[#c9a22744] text-[#c9a227] hover:bg-[#c9a22711] transition-colors whitespace-nowrap">+ New</button>
        </div>
        {linkedProperty && (
          <div className="p-3 rounded-lg bg-[#1a1b30] border border-[#2e3060] text-xs text-[#a2a2c0] grid grid-cols-3 gap-2">
            <div><span className="text-[#62647c]">Type:</span> {linkedProperty.property_type || "—"}</div>
            <div><span className="text-[#62647c]">Beds:</span> {linkedProperty.bedrooms ?? "—"}</div>
            <div><span className="text-[#62647c]">Baths:</span> {linkedProperty.bathrooms ?? "—"}</div>
            <div><span className="text-[#62647c]">Sq ft:</span> {linkedProperty.sq_ft ?? "—"}</div>
            <div><span className="text-[#62647c]">Tenure:</span> {linkedProperty.tenure || "—"}</div>
            <div><span className="text-[#62647c]">EPC:</span> {linkedProperty.epc_rating || "—"}</div>
          </div>
        )}

        {/* Contacts */}
        <SectionHeader title="Contacts" />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Vendor" value={form.vendorContactId} onChange={sf("vendorContactId")} options={contactOptions("vendor")} />
          <Select label="Estate Agent" value={form.agentContactId} onChange={sf("agentContactId")} options={contactOptions("agent")} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Solicitor" value={form.solicitorId} onChange={sf("solicitorId")} options={contactOptions("solicitor")} />
          <Select label="Mortgage Broker" value={form.brokerContactId} onChange={sf("brokerContactId")} options={contactOptions("broker")} />
        </div>

        {/* Notes */}
        <SectionHeader title="Notes" />
        <Field label="" value={form.notes} onChange={sf("notes")} textarea placeholder="Add notes…" />

        <div className="flex gap-3 pt-1">
          <GoldButton onClick={handleSave} label="Save Changes" />
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-xs font-bold tracking-wider uppercase text-[#6a6a82] border border-[#2e3060] hover:border-[#4a4a70] transition-colors">Cancel</button>
        </div>
      </div>

      {showCreateProperty && (
        <PropertyModal
          onSave={async (data) => {
            const prop = await onCreateProperty(data);
            if (prop) sf("propertyId")(prop.id);
            setShowCreateProperty(false);
          }}
          onClose={() => setShowCreateProperty(false)}
        />
      )}
    </div>
  );
}

// ─── Deals Tab ───
function DealsTab({ deals, onEdit, onRemove, onSaveDeal, apiKey, epcEmail, epcKey, contacts, properties, onCreateProperty }) {
  const [subTab, setSubTab] = useState("all");
  const [sortKey, setSortKey] = useState("savedAt");
  const [sortDir, setSortDir] = useState("desc");
  const [editDeal, setEditDeal] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  const handleSaveDeal = (deal) => {
    onSaveDeal(deal);
    setSubTab("all");
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const q = search.toLowerCase().trim();
  const filtered = deals.filter(d => {
    const matchesStatus = filterStatus === "all" || d.pipelineStatus === filterStatus;
    const matchesSearch = !q || [d.address, d.strategy, d.type].some(v => v?.toLowerCase().includes(q));
    return matchesStatus && matchesSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "string") { av = av.toLowerCase(); bv = bv.toLowerCase(); }
    return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const SortTh = ({ label, k }) => (
    <th className="pb-2 pr-4 font-semibold uppercase tracking-wider cursor-pointer select-none hover:text-[#c9a227] transition-colors whitespace-nowrap"
      onClick={() => toggleSort(k)}>
      {label}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );

  const stageColors = Object.fromEntries(PIPELINE_STAGES.map(s => [s.id, s.color]));

  return (
    <div className="space-y-5">
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: "#111128" }}>
        {[{ id: "all", label: "📋 All Deals" }, { id: "analyzer", label: "➕ New Deal" }].map(s => (
          <button key={s.id} onClick={() => setSubTab(s.id)} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all" style={{ background: subTab === s.id ? "linear-gradient(135deg,#2e3044,#373958)" : "transparent", color: subTab === s.id ? "#c9a227" : "#a2a2c0", boxShadow: subTab === s.id ? "0 2px 12px rgba(0,0,0,0.2)" : "none" }}>{s.label}</button>
        ))}
      </div>

      {subTab === "analyzer" && <DealAnalyzer apiKey={apiKey} onSaveDeal={handleSaveDeal} epcEmail={epcEmail} epcKey={epcKey} contacts={contacts} />}

      {subTab === "all" && <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#faf8ff]" style={{ fontFamily: "'Playfair Display', serif" }}>Deals</h2>
          <p className="text-sm text-[#a2a2c0]">{filtered.length}{filtered.length !== deals.length ? ` of ${deals.length}` : ""} deal{deals.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="relative">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search address, strategy…"
            className="bg-[#111128] border border-[#252545] rounded-lg pl-8 pr-3 py-2 text-xs text-[#e0dfe4] placeholder-[#404060] focus:outline-none focus:border-[#c9a227] w-56 transition-colors"
          />
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#404060] text-xs">🔍</span>
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#404060] hover:text-[#f87171] text-xs transition-colors">✕</button>}
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => setFilterStatus("all")} className="px-3 py-1.5 rounded-lg text-[10px] font-bold tracking-wider uppercase transition-all" style={{ background: filterStatus === "all" ? "#c9a227" : "#252737", color: filterStatus === "all" ? "#171829" : "#a2a2c0", border: `1px solid ${filterStatus === "all" ? "transparent" : "#373954"}` }}>All</button>
        {PIPELINE_STAGES.map(s => (
          <button key={s.id} onClick={() => setFilterStatus(s.id)} className="px-3 py-1.5 rounded-lg text-[10px] font-bold tracking-wider uppercase transition-all" style={{ background: filterStatus === s.id ? s.color + "33" : "#252737", color: filterStatus === s.id ? s.color : "#a2a2c0", border: `1px solid ${filterStatus === s.id ? s.color + "66" : "#373954"}` }}>{s.label}</button>
        ))}
      </div>

      {sorted.length === 0 && (
        <div className="text-center py-16 space-y-2">
          <div className="text-4xl">📋</div>
          <div className="text-sm font-semibold text-[#62647c]">No deals yet</div>
          <div className="text-xs text-[#505270]">Save a deal from the Deal Analyzer to get started</div>
        </div>
      )}

      {sorted.length > 0 && (
        <Section>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#a2a2c0] text-left border-b border-[#373954]">
                  <SortTh label="Address" k="address" />
                  <SortTh label="Asking" k="price" />
                  <SortTh label="Offer" k="offerPrice" />
                  <SortTh label="ARV" k="arv" />
                  <SortTh label="Profit" k="profit" />
                  <SortTh label="Yield" k="yield" />
                  <SortTh label="Strategy" k="strategy" />
                  <SortTh label="Score" k="score" />
                  <SortTh label="Status" k="pipelineStatus" />
                  <th className="pb-2 font-semibold uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(d => {
                  const scoreColor = d.score >= 7 ? "#4ade80" : d.score >= 4 ? "#facc15" : "#f87171";
                  return (
                    <tr key={d.id} className="border-b border-[#1f2135] hover:bg-[#1a1b30] transition-colors group cursor-pointer" onClick={() => setEditDeal(d)}>
                      <td className="py-3 pr-4 font-semibold text-[#e8e6f4] max-w-[180px]">
                        <div className="truncate">{d.address}</div>
                        {d.type && <div className="text-[10px] text-[#6a6a82] font-normal">{d.type}</div>}
                      </td>
                      <td className="py-3 pr-4 text-[#b8b8d0]">{fmt(d.price) || "—"}</td>
                      <td className="py-3 pr-4 text-[#c9a227] font-semibold">{d.offerPrice ? fmt(d.offerPrice) : "—"}</td>
                      <td className="py-3 pr-4 text-[#b8b8d0]">{fmt(d.arv) || "—"}</td>
                      <td className="py-3 pr-4 font-bold text-[#4ade80]">{fmt(d.profit) || "—"}</td>
                      <td className="py-3 pr-4 text-[#b8b8d0]">{d.yield ? pct(d.yield) : "—"}</td>
                      <td className="py-3 pr-4 text-[#b8b8d0]">{d.strategy || "—"}</td>
                      <td className="py-3 pr-4">
                        {d.score ? <span className="font-bold text-xs px-2 py-0.5 rounded-lg" style={{ background: scoreColor + "22", color: scoreColor }}>{d.score}</span> : "—"}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: (stageColors[d.pipelineStatus] || "#a2a2c0") + "22", color: stageColors[d.pipelineStatus] || "#a2a2c0" }}>
                          {PIPELINE_STAGES.find(s => s.id === d.pipelineStatus)?.label || d.pipelineStatus}
                        </span>
                      </td>
                      <td className="py-3">
                        <button onClick={e => { e.stopPropagation(); onRemove(d.id); }} className="text-[#62647c] hover:text-[#f87171] transition-colors opacity-0 group-hover:opacity-100">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      </>}

      {editDeal && (
        <DealEditModal
          deal={editDeal}
          contacts={contacts}
          properties={properties}
          onCreateProperty={onCreateProperty}
          onSave={(fields) => { onEdit(editDeal.id, fields); setEditDeal(null); }}
          onClose={() => setEditDeal(null)}
        />
      )}
    </div>
  );
}

// ─── Valuation Tab ───
function ValuationTab({ apiKey, deals, userId }) {
  const [subTab, setSubTab] = useState("evaluator");
  const subTabs = [
    { id: "evaluator", label: "Evaluator" },
    { id: "ai_comps", label: "AI Comparables" },
    { id: "comparables", label: "Comparables" },
  ];
  return (
    <div className="space-y-6">
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: "#111128" }}>
        {subTabs.map(s => (
          <button key={s.id} onClick={() => setSubTab(s.id)} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all" style={{ background: subTab === s.id ? "linear-gradient(135deg,#2e3044,#373958)" : "transparent", color: subTab === s.id ? "#c9a227" : "#a2a2c0", boxShadow: subTab === s.id ? "0 2px 12px rgba(0,0,0,0.2)" : "none" }}>{s.label}</button>
        ))}
      </div>
      {subTab === "evaluator" && <PropertyEvaluator apiKey={apiKey} />}
      {subTab === "ai_comps" && <Comparables apiKey={apiKey} />}
      {subTab === "comparables" && <DealComparables deals={deals} userId={userId} />}
    </div>
  );
}

// ─── Auction Prep ───
function AuctionPrep({ apiKey }) {
  const [auctionTab, setAuctionTab] = useState("calc");

  // Calculator state
  const [guidePrice, setGuidePrice] = useState("");
  const [buyersPremPct, setBuyersPremPct] = useState("1.5");
  const [vatOnPrem, setVatOnPrem] = useState(true);
  const [legalPackCost, setLegalPackCost] = useState("300");
  const [surveyCost, setSurveyCost] = useState("500");
  const [bridgingPct, setBridgingPct] = useState("0.85");
  const [bridgingMonths, setBridgingMonths] = useState("3");
  const [auctionArv, setAuctionArv] = useState("");
  const [auctionRepair, setAuctionRepair] = useState("");

  // Catalogue analyzer state
  const [lotDesc, setLotDesc] = useState("");
  const [catGuidePrice, setCatGuidePrice] = useState("");
  const [catArv, setCatArv] = useState("");
  const [catRepair, setCatRepair] = useState("");
  const [catResult, setCatResult] = useState("");
  const [catLoading, setCatLoading] = useState(false);

  // Checklist state
  const [lotType, setLotType] = useState("Residential");
  const [auctionDate, setAuctionDate] = useState("");
  const [checkResult, setCheckResult] = useState("");
  const [checkLoading, setCheckLoading] = useState(false);

  // Calculator derived values
  const gp = parseFloat(guidePrice) || 0;
  const arvN = parseFloat(auctionArv) || 0;
  const repN = parseFloat(auctionRepair) || 0;
  const premPct = parseFloat(buyersPremPct) || 1.5;
  const premRaw = gp * (premPct / 100);
  const premTotal = vatOnPrem ? premRaw * 1.2 : premRaw;
  const sdlt = calcSDLT(gp, true);
  const bridgePctN = parseFloat(bridgingPct) || 0.85;
  const bridgeMonthsN = parseFloat(bridgingMonths) || 3;
  const bridgingCost = gp * (bridgePctN / 100) * bridgeMonthsN;
  const legalN = parseFloat(legalPackCost) || 300;
  const surveyN = parseFloat(surveyCost) || 500;
  const totalAcqCost = gp + premTotal + sdlt + legalN + surveyN + bridgingCost;
  const maxBid70 = arvN > 0 ? arvN * 0.7 - repN - premTotal - sdlt - legalN - surveyN - bridgingCost : 0;
  const costPerPound = gp > 0 ? totalAcqCost / gp : 0;

  const runCatalogueAI = async () => {
    if (!lotDesc || !apiKey) return;
    setCatLoading(true);
    setCatResult("");
    try {
      const gp2 = parseFloat(catGuidePrice) || 0;
      const arv2 = parseFloat(catArv) || 0;
      const rep2 = parseFloat(catRepair) || 0;
      const estPrem = gp2 * 0.015 * 1.2;
      const estSdlt = calcSDLT(gp2, true);
      const estBridge = gp2 * 0.0085 * 3;
      const estCosts = estPrem + estSdlt + estBridge + 300 + 500;
      const maxBid = arv2 > 0 ? arv2 * 0.7 - rep2 - estCosts : 0;

      const sys = `You are an expert UK property auction analyst with deep knowledge of UK auction law, bridging finance, and investment due diligence. Be specific and brutally practical. Always use £.`;
      const msg = `Analyse this UK auction lot for investment potential:

LOT DESCRIPTION:
${lotDesc}

FINANCIALS:
— Guide Price: ${fmt(gp2)}
— Estimated ARV: ${arv2 ? fmt(arv2) : "Not provided"}
— Estimated Repairs: ${rep2 ? fmt(rep2) : "Not provided"}
— Estimated Auction Costs (buyer's premium + SDLT + bridging + legal): ${fmt(Math.round(estCosts))}
— 70% Rule Max Bid (calculated): ${maxBid > 0 ? fmt(Math.round(maxBid)) : "Insufficient data"}

Provide:

1. MAX BID RECOMMENDATION
Recommended maximum bid using the 70% rule adjusted for all auction costs. Show your working. Include a "walk away" price.

2. RISK FLAGS
Identify and rate each risk (Low/Medium/High):
— Title and legal risks
— Structural risks
— Planning and permitted development risks
— Leasehold risks (if applicable)
— Flood and environmental risks
— Market and exit risks

3. 48-HOUR DUE DILIGENCE CHECKLIST
Specific actions for the next 48 hours before the auction, in priority order.

4. FINANCING STRATEGY
Recommended financing approach for this lot — bridging lender suggestions, typical LTV, rates, and exit to long-term finance.`;
      const r = await callClaude(apiKey, sys, msg);
      setCatResult(r);
    } catch (e) {
      setCatResult("⚠️ " + e.message);
    }
    setCatLoading(false);
  };

  const runChecklistAI = async () => {
    if (!apiKey) return;
    setCheckLoading(true);
    setCheckResult("");
    try {
      const sys = `You are an expert UK auction solicitor and property investor. You know every step needed to safely bid at UK property auctions. Be specific and actionable.`;
      const msg = `Generate a comprehensive 48-hour pre-auction checklist for this lot:

— Lot Type: ${lotType}
— Auction Date: ${auctionDate || "Not specified — use generic timeline"}

Cover:
1. LEGAL PACK REVIEW — What to check, what red flags to look for, which solicitor to instruct
2. SURVEY / INSPECTION — What type of survey, how to arrange a viewing, what to inspect
3. FINANCING CONFIRMATION — Bridging lender confirmation, proof of funds, deposit requirements (typically 10% on the day)
4. BID LIMIT CALCULATION — How to set your maximum bid, factoring in all costs
5. BIDDING STRATEGY — How to bid at auction, when to stop, proxy bidding, online vs in-room
6. DAY-OF CHECKLIST — What to bring, what to do if you win, what happens next
7. POST-AUCTION — Exchange, completion timeline, bridging drawdown, refurb start

Be extremely specific for ${lotType} lots. Include typical UK auction house rules and the legal implications of winning a bid.`;
      const r = await callClaude(apiKey, sys, msg);
      setCheckResult(r);
    } catch (e) {
      setCheckResult("⚠️ " + e.message);
    }
    setCheckLoading(false);
  };

  const auctionTabs = [
    { id: "calc", label: "Cost Calculator" },
    { id: "catalogue", label: "Catalogue Analyzer" },
    { id: "checklist", label: "Pre-Auction Checklist" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#faf8ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          Auction Prep
        </h2>
        <p className="text-sm text-[#a2a2c0]">UK auction cost calculator, lot analyser, and pre-auction checklist</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {auctionTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setAuctionTab(t.id)}
            className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all"
            style={{
              background: auctionTab === t.id ? "#c9a22722" : "transparent",
              color: auctionTab === t.id ? "#c9a227" : "#a2a2c0",
              border: `1px solid ${auctionTab === t.id ? "#c9a22744" : "#373954"}`,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {auctionTab === "calc" && (
        <Section title="Auction Cost Calculator">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Lot Guide Price" value={guidePrice} onChange={setGuidePrice} placeholder="200000" prefix="£" />
              <Field label="Buyer's Premium" value={buyersPremPct} onChange={setBuyersPremPct} placeholder="1.5" suffix="%" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Legal Pack Cost" value={legalPackCost} onChange={setLegalPackCost} placeholder="300" prefix="£" />
              <Field label="Survey Cost" value={surveyCost} onChange={setSurveyCost} placeholder="500" prefix="£" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Bridging Rate" value={bridgingPct} onChange={setBridgingPct} placeholder="0.85" suffix="% / mo" />
              <Field label="Bridging Term" value={bridgingMonths} onChange={setBridgingMonths} placeholder="3" suffix="months" />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setVatOnPrem(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{ background: vatOnPrem ? "#c9a22722" : "#1f2135", color: vatOnPrem ? "#c9a227" : "#a2a2c0", border: `1px solid ${vatOnPrem ? "#c9a22744" : "#3e4060"}` }}
              >
                <span className={`w-3 h-3 rounded-full border-2 ${vatOnPrem ? "bg-[#c9a227] border-[#c9a227]" : "border-[#444]"}`} />
                VAT on Premium (×1.2)
              </button>
              <button
                onClick={() => setVatOnPrem(false)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{ background: !vatOnPrem ? "#c9a22722" : "#1f2135", color: !vatOnPrem ? "#c9a227" : "#a2a2c0", border: `1px solid ${!vatOnPrem ? "#c9a22744" : "#3e4060"}` }}
              >
                <span className={`w-3 h-3 rounded-full border-2 ${!vatOnPrem ? "bg-[#c9a227] border-[#c9a227]" : "border-[#444]"}`} />
                No VAT
              </button>
            </div>
            <div className="border-t border-[#2e3044] pt-4">
              <p className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0] mb-3">70% Rule (optional)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="ARV (After Repair Value)" value={auctionArv} onChange={setAuctionArv} placeholder="300000" prefix="£" />
                <Field label="Estimated Repairs" value={auctionRepair} onChange={setAuctionRepair} placeholder="40000" prefix="£" />
              </div>
            </div>

            {gp > 0 && (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard label="Buyer's Premium (inc. VAT)" value={fmt(Math.round(premTotal))} accent />
                  <StatCard label="SDLT" value={fmt(sdlt)} sub="Additional property rate" />
                  <StatCard label="Bridging Finance" value={fmt(Math.round(bridgingCost))} sub={`${bridgeMonthsN}mo @ ${bridgePctN}%/mo`} />
                  <StatCard label="Total Acquisition Cost" value={fmt(Math.round(totalAcqCost))} />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-2 gap-3">
                  <StatCard label="Cost Per £1 Spent" value={`£${costPerPound.toFixed(3)}`} sub="All-in cost ratio" />
                  {arvN > 0 && (
                    <StatCard
                      label="70% Rule Max Bid"
                      value={maxBid70 > 0 ? fmt(Math.round(maxBid70)) : "Insufficient data"}
                      sub={maxBid70 > gp ? "Guide price is below max bid ✓" : maxBid70 > 0 ? "Guide price exceeds max bid ✗" : "Enter ARV and repairs"}
                      accent={maxBid70 > 0}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {auctionTab === "catalogue" && (
        <Section title="Catalogue Lot Analyzer">
          <div className="space-y-4">
            <p className="text-xs text-[#a2a2c0]">Paste the auction catalogue lot description. Claude will analyse the deal and return a max bid recommendation, risk flags, and due diligence checklist.</p>
            <Field label="Lot Description" value={lotDesc} onChange={setLotDesc} placeholder="Paste the full auction catalogue description here…" textarea />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Guide Price" value={catGuidePrice} onChange={setCatGuidePrice} placeholder="200000" prefix="£" />
              <Field label="Estimated ARV" value={catArv} onChange={setCatArv} placeholder="300000" prefix="£" />
              <Field label="Estimated Repairs" value={catRepair} onChange={setCatRepair} placeholder="40000" prefix="£" />
            </div>
            <GoldButton onClick={runCatalogueAI} loading={catLoading} label="Analyse This Lot" />
          </div>
        </Section>
      )}

      {auctionTab === "catalogue" && <ResultBox result={catResult} title="Lot Analysis" />}

      {auctionTab === "checklist" && (
        <Section title="Pre-Auction Checklist Generator">
          <div className="space-y-4">
            <p className="text-xs text-[#a2a2c0]">Generate a personalised 48-hour pre-auction checklist tailored to your lot type.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Lot Type"
                value={lotType}
                onChange={setLotType}
                options={["Residential", "Commercial", "Land", "Mixed Use", "HMO"]}
              />
              <Field label="Auction Date" value={auctionDate} onChange={setAuctionDate} placeholder="e.g. 15 April 2026" type="text" />
            </div>
            <GoldButton onClick={runChecklistAI} loading={checkLoading} label="Generate Checklist" />
          </div>
        </Section>
      )}

      {auctionTab === "checklist" && <ResultBox result={checkResult} title="Pre-Auction Checklist" />}
    </div>
  );
}

// ─── Settings Modal ───
function SettingsModal({ apiKey, setApiKey, epcEmail, setEpcEmail, epcKey, setEpcKey, onClose, deals, onClearDeals }) {
  const [key, setKey] = useState(apiKey);
  const [email, setEmail] = useState(epcEmail);
  const [epc, setEpc] = useState(epcKey);
  const [confirmClear, setConfirmClear] = useState(false);
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  const save = () => {
    setApiKey(key);
    setEpcEmail(email);
    setEpcKey(epc);
    onClose();
  };

  const exportCSV = () => {
    if (!deals.length) return;
    const headers = ["Address", "Price", "ARV", "Repair Costs", "Est. Profit", "Deal Score", "Type", "Strategy", "Gross Yield %", "Pipeline Status", "Saved At"];
    const rows = deals.map((d) => [
      `"${(d.address || "").replace(/"/g, '""')}"`,
      d.price || 0, d.arv || 0, d.repair || 0, d.profit || 0, d.score || 0,
      `"${d.type || ""}"`, `"${d.strategy || ""}"`,
      (d.yield || 0).toFixed(1),
      `"${d.pipelineStatus || "researching"}"`,
      `"${d.savedAt || ""}"`,
    ].join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "deal-pipeline.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-md shadow-2xl space-y-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div>
          <h3 className="text-lg font-bold text-[#f5f4ff] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Settings</h3>
          <p className="text-xs text-[#a2a2c0]">Keys and deals stored in browser localStorage — persist between sessions.</p>
        </div>
        <div className="space-y-3">
          <p className="text-[10px] font-bold tracking-[.15em] uppercase text-[#c9a227]">AI</p>
          <Field label="Anthropic API Key" value={key} onChange={setKey} placeholder="sk-ant-…" type="password" />
        </div>
        <div className="space-y-3">
          <p className="text-[10px] font-bold tracking-[.15em] uppercase text-[#c9a227]">EPC Register</p>
          <Field label="EPC Email (registered account)" value={email} onChange={setEmail} placeholder="you@email.com" />
          <Field label="EPC API Key" value={epc} onChange={setEpc} placeholder="API key from epc.opendatacommunities.org" type="password" />
        </div>
        <div className="flex gap-3">
          <GoldButton onClick={save} label="Save Settings" small />
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-xs font-bold tracking-wider uppercase text-[#a2a2c0] border border-[#3e4060] hover:border-[#5c5e80] transition-colors">
            Cancel
          </button>
        </div>
        <div className="pt-3 border-t border-[#3e4060] space-y-2">
          <p className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Data Management</p>
          <div className="flex gap-2">
            <button
              onClick={exportCSV}
              disabled={!deals.length}
              className="flex-1 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#c9a227] border border-[#c9a22744] hover:bg-[#c9a22711] transition-colors disabled:opacity-40"
            >
              Export CSV {deals.length > 0 ? `(${deals.length})` : ""}
            </button>
            {!confirmClear ? (
              <button
                onClick={() => setConfirmClear(true)}
                disabled={!deals.length}
                className="flex-1 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#f87171] border border-[#f8717144] hover:bg-[#f8717111] transition-colors disabled:opacity-40"
              >
                Clear All Data
              </button>
            ) : (
              <button
                onClick={() => { onClearDeals(); setConfirmClear(false); onClose(); }}
                className="flex-1 py-2 rounded-lg text-xs font-bold tracking-wider uppercase bg-[#f87171] text-white transition-colors"
              >
                Confirm Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Contacts & Portfolio & Investors ───

const TYPE_COLORS = {
  vendor: "#c9a227", agent: "#60a5fa", solicitor: "#a78bfa",
  broker: "#34d399", contractor: "#fb923c", investor: "#f472b6", other: "#a2a2c0",
};

// ContactModal
function ContactModal({ initial, onSave, onClose }) {
  const blank = { first_name: "", last_name: "", company_name: "", contact_type: "vendor", email: "", phone: "", website: "", address_line_1: "", address_line_2: "", city: "", postcode: "", specialty: "", notes: "", tags: "" };
  const [f, setF] = useState({ ...blank, ...initial, tags: Array.isArray(initial?.tags) ? initial.tags.join(", ") : "" });
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">{initial ? "Edit Contact" : "Add Contact"}</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First Name *" value={f.first_name} onChange={set("first_name")} placeholder="Jane" />
          <Field label="Last Name *" value={f.last_name} onChange={set("last_name")} placeholder="Smith" />
        </div>
        <Field label="Company" value={f.company_name} onChange={set("company_name")} placeholder="Smith & Co" />
        <Select label="Type *" value={f.contact_type} onChange={set("contact_type")} options={["vendor","agent","solicitor","broker","contractor","investor","other"]} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email" value={f.email} onChange={set("email")} placeholder="jane@email.com" type="email" />
          <Field label="Phone" value={f.phone} onChange={set("phone")} placeholder="07700 000000" />
        </div>
        <PostcodeLookup
          postcode={f.postcode}
          onPostcodeChange={set("postcode")}
          onSelect={(a) => setF(p => ({ ...p, address_line_1: a.line1, address_line_2: a.line2, city: a.city }))}
        />
        <Field label="Address Line 1" value={f.address_line_1} onChange={set("address_line_1")} placeholder="12 High Street" />
        <Field label="Address Line 2" value={f.address_line_2} onChange={set("address_line_2")} placeholder="Flat 3" />
        <Field label="City" value={f.city} onChange={set("city")} placeholder="Birmingham" />
        <Field label="Website" value={f.website} onChange={set("website")} placeholder="https://..." />
        {f.contact_type === "contractor" && (
          <Select label="Specialty" value={f.specialty} onChange={set("specialty")} options={["","Plumbing","Electrical","Roofing","Building / General","Decoration","Flooring","Plastering","Joinery / Carpentry","Heating / HVAC","Glazing / Windows","Landscaping","Damp / Waterproofing","Other"]} />
        )}
        <Field label="Notes" value={f.notes} onChange={set("notes")} placeholder="…" textarea />
        <Field label="Tags" value={f.tags} onChange={set("tags")} placeholder="bmv, motivated-seller, hmo" />
        <GoldButton label={initial ? "Save Changes" : "Add Contact"} onClick={() => { onSave({ ...f, tags: f.tags ? f.tags.split(",").map(t => t.trim()).filter(Boolean) : [] }); onClose(); }} />
      </div>
    </div>
  );
}

// InteractionModal
function InteractionModal({ contactId, onSave, onClose }) {
  const [f, setF] = useState({ interaction_type: "call", direction: "outbound", subject: "", body: "", outcome: "neutral", follow_up_date: "" });
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">Log Interaction</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Type" value={f.interaction_type} onChange={set("interaction_type")} options={["call","email","sms","viewing","meeting","offer","letter","note"]} />
          <Select label="Direction" value={f.direction} onChange={set("direction")} options={["inbound","outbound"]} />
        </div>
        <Field label="Subject" value={f.subject} onChange={set("subject")} placeholder="Follow-up call" />
        <Field label="Notes / Body" value={f.body} onChange={set("body")} placeholder="What was discussed…" textarea />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Outcome" value={f.outcome} onChange={set("outcome")} options={["positive","neutral","negative","no_answer","callback_requested"]} />
          <Field label="Follow-up Date" value={f.follow_up_date} onChange={set("follow_up_date")} type="date" />
        </div>
        <GoldButton label="Log Interaction" onClick={() => { onSave({ ...f, contact_id: contactId }); onClose(); }} />
      </div>
    </div>
  );
}

// ContactsTab
function ContactsTab({ contacts, onAdd, onUpdate, onDelete, onArchive, apiKey }) {
  const [subTab, setSubTab] = useState("contacts");
  const [showAdd, setShowAdd] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [interactions, setInteractions] = useState({});
  const [showAddInteraction, setShowAddInteraction] = useState(null);
  const [filterType, setFilterType] = useState("all");

  const filtered = filterType === "all" ? contacts : contacts.filter(c => c.contact_type === filterType);

  const loadInteractions = async (contactId) => {
    if (interactions[contactId]) return;
    const { data } = await supabase.from("interactions").select("*").eq("contact_id", contactId).order("created_at", { ascending: false });
    setInteractions(p => ({ ...p, [contactId]: data || [] }));
  };

  const handleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    loadInteractions(id);
  };

  const logInteraction = async (data) => {
    const { data: row } = await supabase.from("interactions").insert(data).select().single();
    if (row) setInteractions(p => ({ ...p, [data.contact_id]: [row, ...(p[data.contact_id] || [])] }));
  };

  const deleteInteraction = async (contactId, id) => {
    await supabase.from("interactions").delete().eq("id", id);
    setInteractions(p => ({ ...p, [contactId]: (p[contactId] || []).filter(x => x.id !== id) }));
  };

  const filterTabs = ["all","vendor","agent","solicitor","broker","contractor","investor","other"];

  return (
    <div className="space-y-5">
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: "#111128" }}>
        {[{ id: "contacts", label: "👥 Contacts" }, { id: "outreach", label: "✉️ Outreach" }].map(s => (
          <button key={s.id} onClick={() => setSubTab(s.id)} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all" style={{ background: subTab === s.id ? "linear-gradient(135deg,#2e3044,#373958)" : "transparent", color: subTab === s.id ? "#c9a227" : "#a2a2c0", boxShadow: subTab === s.id ? "0 2px 12px rgba(0,0,0,0.2)" : "none" }}>{s.label}</button>
        ))}
      </div>

      {subTab === "outreach" && <SellerOutreach apiKey={apiKey} />}

      {subTab === "contacts" && <>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#f5f4ff]">Contacts <span className="text-[#a2a2c0] text-sm font-normal">({contacts.length})</span></h2>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#171829] transition-all" style={{ background: "linear-gradient(135deg,#c9a227,#d4af37)" }}>+ Add Contact</button>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {filterTabs.map(t => (
          <button key={t} onClick={() => setFilterType(t)} className="px-3 py-1.5 rounded-lg text-[10px] font-bold tracking-wider uppercase transition-all" style={{ background: filterType === t ? TYPE_COLORS[t] || "#c9a227" : "#252737", color: filterType === t ? "#171829" : "#a2a2c0", border: `1px solid ${filterType === t ? "transparent" : "#373954"}` }}>{t}</button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 space-y-2">
          <div className="text-4xl">👥</div>
          <div className="text-sm font-semibold text-[#62647c]">No contacts yet</div>
          <div className="text-xs text-[#505270]">Add vendors, agents and solicitors to track your network</div>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(c => (
          <div key={c.id} className="bg-[#252737] border border-[#373954] rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#2b2d42] transition-colors" onClick={() => handleExpand(c.id)}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: TYPE_COLORS[c.contact_type] + "33", color: TYPE_COLORS[c.contact_type] }}>
                {(c.first_name?.[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#f5f4ff]">{c.first_name} {c.last_name}</div>
                {c.company_name && <div className="text-xs text-[#a2a2c0] truncate">{c.company_name}</div>}
                <div className="flex items-center gap-3 mt-0.5">
                  {c.email && <span className="text-[10px] text-[#787898] truncate">{c.email}</span>}
                  {c.phone && <span className="text-[10px] text-[#787898]">{c.phone}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: TYPE_COLORS[c.contact_type] + "22", color: TYPE_COLORS[c.contact_type] }}>{c.contact_type}</span>
                <button onClick={(e) => { e.stopPropagation(); setEditContact(c); }} className="text-[#62647c] hover:text-[#c9a227] transition-colors text-sm p-1">✏️</button>
                <button onClick={(e) => { e.stopPropagation(); if (window.confirm("Archive this contact? They will be hidden but preserved in the database.")) onArchive(c.id); }} title="Archive contact" className="text-[#62647c] hover:text-[#facc15] transition-colors text-sm p-1">📦</button>
                <button onClick={(e) => { e.stopPropagation(); if (window.confirm("Permanently delete this contact? This cannot be undone.")) onDelete(c.id); }} className="text-[#62647c] hover:text-[#f87171] transition-colors text-sm p-1">✕</button>
              </div>
            </div>
            {expandedId === c.id && (
              <div className="border-t border-[#373954] p-4 space-y-4">
                <div className="grid grid-cols-2 gap-2 text-xs text-[#a2a2c0]">
                  {c.city && <div><span className="text-[#62647c]">City:</span> {c.city}</div>}
                  {c.postcode && <div><span className="text-[#62647c]">Postcode:</span> {c.postcode}</div>}
                  {c.specialty && <div><span className="text-[#62647c]">Specialty:</span> {c.specialty}</div>}
                  {c.website && (
                    <div className="col-span-2">
                      <span className="text-[#62647c]">Website:</span>{" "}
                      <a href={c.website} target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline truncate">{c.website.replace(/^https?:\/\//, "")}</a>
                    </div>
                  )}
                </div>
                {c.notes && <div className="text-xs text-[#a2a2c0]"><span className="text-[#62647c]">Notes:</span> {c.notes}</div>}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Interactions</span>
                    <button onClick={() => setShowAddInteraction(c.id)} className="text-[10px] font-bold text-[#c9a227] hover:text-[#d4af37]">+ Log</button>
                  </div>
                  {(interactions[c.id] || []).length === 0 && <div className="text-xs text-[#505270]">No interactions logged yet</div>}
                  <div className="space-y-1.5">
                    {(interactions[c.id] || []).map(i => (
                      <div key={i.id} className="flex items-start gap-2 bg-[#1f2135] rounded-lg p-2.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-bold text-[#c9a227] uppercase">{i.interaction_type}</span>
                            <span className="text-[10px] text-[#787898]">{i.direction}</span>
                            {i.subject && <span className="text-xs text-[#c4c4da] truncate">{i.subject}</span>}
                          </div>
                          {i.outcome && <span className="text-[9px] text-[#a2a2c0]">{i.outcome}</span>}
                          <div className="text-[9px] text-[#62647c] mt-0.5">{new Date(i.created_at).toLocaleDateString("en-GB")}</div>
                        </div>
                        <button onClick={() => deleteInteraction(c.id, i.id)} className="text-[#62647c] hover:text-[#f87171] transition-colors text-sm shrink-0">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && <ContactModal onSave={onAdd} onClose={() => setShowAdd(false)} />}
      {editContact && <ContactModal initial={editContact} onSave={(data) => { onUpdate(editContact.id, data); setEditContact(null); }} onClose={() => setEditContact(null)} />}
      {showAddInteraction && <InteractionModal contactId={showAddInteraction} onSave={logInteraction} onClose={() => setShowAddInteraction(null)} />}
      </>}
    </div>
  );
}

// DealExpensesModal
function DealExpensesModal({ deal, onClose }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: "refurb", description: "", amount: "", date_incurred: "", paid: false });
  const [editExpense, setEditExpense] = useState(null);
  const [saveError, setSaveError] = useState("");
  const sf = (k) => (v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    supabase.from("deal_expenses").select("*").eq("deal_id", deal.id).order("created_at", { ascending: false })
      .then(({ data, error }) => { if (!error) setExpenses(data || []); setLoading(false); });
  }, [deal.id]);

  const addExpense = async () => {
    if (!form.amount) return;
    setSaveError("");
    const { data: row, error } = await supabase.from("deal_expenses").insert({ ...form, deal_id: deal.id, amount: Number(form.amount) }).select().single();
    if (error) { setSaveError(error.message); return; }
    if (row) setExpenses(p => [row, ...p]);
    setForm({ category: "refurb", description: "", amount: "", date_incurred: "", paid: false });
  };

  const updateExpense = async (id, data) => {
    const { data: row } = await supabase.from("deal_expenses").update(data).eq("id", id).select().single();
    if (row) setExpenses(ps => ps.map(e => e.id === id ? row : e));
    setEditExpense(null);
  };

  const deleteExpense = async (id) => {
    await supabase.from("deal_expenses").delete().eq("id", id);
    setExpenses(ps => ps.filter(e => e.id !== id));
  };

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const paid = expenses.filter(e => e.paid).reduce((s, e) => s + (e.amount || 0), 0);

  const CAT_COLORS = { refurb: "#fb923c", legal: "#60a5fa", survey: "#a78bfa", sdlt: "#f472b6", broker: "#34d399", insurance: "#facc15", other: "#a2a2c0" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-2xl shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">Deal Expenses</h3>
            <div className="text-[10px] text-[#787898] mt-0.5">{deal.address}</div>
          </div>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Total Expenses" value={fmt(total)} />
          <StatCard label="Paid" value={fmt(paid)} accent />
          <StatCard label="Outstanding" value={fmt(total - paid)} sub={(total - paid) > 0 ? "Unpaid" : "Clear"} />
        </div>
        {loading ? <div className="text-xs text-[#a2a2c0]">Loading…</div> : (
          <div className="space-y-1.5">
            {expenses.length === 0 && <div className="text-xs text-center text-[#505270] py-4">No expenses logged yet</div>}
            {expenses.map(e => editExpense?.id === e.id ? (
              <div key={e.id} className="bg-[#1f2135] rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Select label="Category" value={editExpense.category} onChange={(v) => setEditExpense(ep => ({ ...ep, category: v }))} options={["refurb","legal","survey","sdlt","broker","insurance","other"]} />
                  <Field label="Amount" value={String(editExpense.amount)} onChange={(v) => setEditExpense(ep => ({ ...ep, amount: v }))} prefix="£" type="number" />
                  <Field label="Date" value={editExpense.date_incurred || ""} onChange={(v) => setEditExpense(ep => ({ ...ep, date_incurred: v }))} type="date" />
                </div>
                <Field label="Description" value={editExpense.description || ""} onChange={(v) => setEditExpense(ep => ({ ...ep, description: v }))} />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-[10px] text-[#a2a2c0] cursor-pointer">
                    <input type="checkbox" checked={editExpense.paid} onChange={(e) => setEditExpense(ep => ({ ...ep, paid: e.target.checked }))} className="accent-[#c9a227]" />
                    Paid
                  </label>
                  <button onClick={() => updateExpense(e.id, { category: editExpense.category, description: editExpense.description, amount: Number(editExpense.amount), date_incurred: editExpense.date_incurred, paid: editExpense.paid })} className="text-[10px] font-bold text-[#4ade80] border border-[#4ade8033] px-3 py-1 rounded">Save</button>
                  <button onClick={() => setEditExpense(null)} className="text-[10px] text-[#62647c] px-3 py-1 rounded">Cancel</button>
                </div>
              </div>
            ) : (
              <div key={e.id} className="bg-[#1f2135] rounded-lg p-2.5 flex items-center gap-2">
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: (CAT_COLORS[e.category] || "#a2a2c0") + "22", color: CAT_COLORS[e.category] || "#a2a2c0" }}>{e.category}</span>
                <span className="text-xs text-[#e0dfe4] flex-1 truncate">{e.description || "—"}</span>
                {e.date_incurred && <span className="text-[9px] text-[#62647c] shrink-0">{e.date_incurred}</span>}
                <span className="text-xs font-bold text-[#c9a227] shrink-0">{fmt(e.amount)}</span>
                <span className="text-[9px] shrink-0" style={{ color: e.paid ? "#4ade80" : "#f87171" }}>{e.paid ? "paid" : "unpaid"}</span>
                <button onClick={() => setEditExpense(e)} className="text-[#62647c] hover:text-[#c9a227] text-xs shrink-0">✏️</button>
                <button onClick={() => deleteExpense(e.id)} className="text-[#62647c] hover:text-[#f87171] text-xs shrink-0">✕</button>
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-[#373954] pt-4 space-y-3">
          <div className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Add Expense</div>
          <div className="grid grid-cols-3 gap-3">
            <Select label="Category" value={form.category} onChange={sf("category")} options={["refurb","legal","survey","sdlt","broker","insurance","other"]} />
            <Field label="Amount" value={String(form.amount)} onChange={sf("amount")} prefix="£" type="number" />
            <Field label="Date" value={form.date_incurred} onChange={sf("date_incurred")} type="date" />
          </div>
          <Field label="Description" value={form.description} onChange={sf("description")} placeholder="e.g. Boiler replacement…" />
          {saveError && <div className="text-xs text-[#f87171] bg-[#f8717111] border border-[#f8717133] rounded-lg px-3 py-2">{saveError}</div>}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-[10px] text-[#a2a2c0] cursor-pointer">
              <input type="checkbox" checked={form.paid} onChange={(e) => setForm(p => ({ ...p, paid: e.target.checked }))} className="accent-[#c9a227]" />
              Already paid
            </label>
            <GoldButton label="Add Expense" onClick={addExpense} />
          </div>
        </div>
      </div>
    </div>
  );
}

// DealInvestorsModal
function DealInvestorsModal({ deal, onClose }) {
  const [dealInvestors, setDealInvestors] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ investor_id: "", amount_invested: "", equity_share: "", agreed_return: "", investment_date: "", status: "active" });
  const [saveError, setSaveError] = useState("");
  const sf = (k) => (v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    Promise.all([
      supabase.from("deal_investors").select("*, investors(id, contact_id, contacts(first_name, last_name, company_name))").eq("deal_id", deal.id),
      supabase.from("investors").select("id, contact_id, contacts(first_name, last_name, company_name)"),
    ]).then(([{ data: di }, { data: inv }]) => {
      setDealInvestors(di || []);
      setInvestors(inv || []);
      setLoading(false);
    });
  }, [deal.id]);

  const addDealInvestor = async () => {
    if (!form.investor_id) return;
    setSaveError("");
    const { data: row, error } = await supabase.from("deal_investors").insert({
      deal_id: deal.id,
      investor_id: form.investor_id,
      amount_invested: Number(form.amount_invested) || null,
      equity_share: Number(form.equity_share) || null,
      agreed_return: Number(form.agreed_return) || null,
      investment_date: form.investment_date || null,
      status: form.status,
    }).select("*, investors(id, contact_id, contacts(first_name, last_name, company_name))").single();
    if (error) { setSaveError(error.message); return; }
    if (row) setDealInvestors(p => [...p, row]);
    setForm({ investor_id: "", amount_invested: "", equity_share: "", agreed_return: "", investment_date: "", status: "active" });
  };

  const deleteDealInvestor = async (id) => {
    await supabase.from("deal_investors").delete().eq("id", id);
    setDealInvestors(p => p.filter(di => di.id !== id));
  };

  const totalInvested = dealInvestors.reduce((s, di) => s + (di.amount_invested || 0), 0);

  const getInvestorName = (di) => {
    const c = di.investors?.contacts;
    return c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "Investor" : "Investor";
  };

  const STATUS_DI = { committed: "#facc15", transferred: "#4ade80", exited: "#a2a2c0" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-xl shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">Deal Investors</h3>
            <div className="text-[10px] text-[#787898] mt-0.5">{deal.address}</div>
          </div>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Investors Linked" value={dealInvestors.length} />
          <StatCard label="Total Invested" value={fmt(totalInvested)} accent />
        </div>
        {loading ? <div className="text-xs text-[#a2a2c0]">Loading…</div> : (
          <div className="space-y-1.5">
            {dealInvestors.length === 0 && <div className="text-xs text-center text-[#505270] py-4">No investors linked yet</div>}
            {dealInvestors.map(di => (
              <div key={di.id} className="bg-[#1f2135] rounded-lg p-2.5 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-[#f5f4ff]">{getInvestorName(di)}</span>
                    {di.status && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: (STATUS_DI[di.status] || "#a2a2c0") + "22", color: STATUS_DI[di.status] || "#a2a2c0" }}>{di.status}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[9px] text-[#787898]">
                    {di.amount_invested ? <span>{fmt(di.amount_invested)} invested</span> : null}
                    {di.equity_share ? <span>{di.equity_share}% equity</span> : null}
                    {di.agreed_return ? <span>{di.agreed_return}% return</span> : null}
                  </div>
                </div>
                <button onClick={() => deleteDealInvestor(di.id)} className="text-[#62647c] hover:text-[#f87171] text-xs shrink-0">✕</button>
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-[#373954] pt-4 space-y-3">
          <div className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Link Investor</div>
          <div className="space-y-2">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold tracking-[.1em] uppercase text-[#787898]">Investor</label>
              <select
                value={form.investor_id}
                onChange={(e) => setForm(p => ({ ...p, investor_id: e.target.value }))}
                className="w-full bg-[#111128] border border-[#252545] rounded-xl px-4 py-2.5 text-sm text-[#e0dfe4] focus:outline-none focus:border-[#c9a227]"
              >
                <option value="">Select investor…</option>
                {investors.map(inv => {
                  const c = inv.contacts;
                  const name = c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "Investor" : "Investor";
                  return <option key={inv.id} value={inv.id}>{name}</option>;
                })}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount Invested" value={String(form.amount_invested)} onChange={sf("amount_invested")} prefix="£" type="number" />
              <Field label="Equity Share %" value={String(form.equity_share)} onChange={sf("equity_share")} suffix="%" type="number" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Agreed Return %" value={String(form.agreed_return)} onChange={sf("agreed_return")} suffix="%" type="number" />
              <Field label="Investment Date" value={form.investment_date} onChange={sf("investment_date")} type="date" />
            </div>
            <Select label="Status" value={form.status} onChange={sf("status")} options={["active","exited","defaulted"]} />
            {saveError && <div className="text-xs text-[#f87171] bg-[#f8717111] border border-[#f8717133] rounded-lg px-3 py-2">{saveError}</div>}
            <GoldButton label="Link Investor" onClick={addDealInvestor} />
          </div>
        </div>
      </div>
    </div>
  );
}

const SOURCED_STATUSES = [
  { id: "found",        label: "Found",        color: "#60a5fa" },
  { id: "offered",      label: "Offered",      color: "#facc15" },
  { id: "under_offer",  label: "Under Offer",  color: "#fb923c" },
  { id: "completed",    label: "Completed",    color: "#4ade80" },
  { id: "fell_through", label: "Fell Through", color: "#f87171" },
];

function SourcedModal({ initial, onSave, onClose, properties, investors, contacts }) {
  const blank = { property_id: "", investor_id: "", status: "found", purchase_price: "", sourcing_fee: "", fee_paid: false, fee_paid_date: "", notes: "" };
  const [f, setF] = useState({ ...blank, ...initial, purchase_price: initial?.purchase_price ?? "", sourcing_fee: initial?.sourcing_fee ?? "", fee_paid_date: initial?.fee_paid_date ?? "" });
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  const investorLabel = (inv) => {
    const c = contacts.find(x => x.id === inv.contact_id);
    return c ? `${c.first_name} ${c.last_name}${c.company_name ? ` — ${c.company_name}` : ""}` : "Unknown";
  };

  const propOptions = [{ value: "", label: "— Select property —" }, ...properties.map(p => ({ value: p.id, label: `${p.address_line_1}${p.city ? `, ${p.city}` : ""}${p.postcode ? ` ${p.postcode}` : ""}` }))];
  const clientOptions = [{ value: "", label: "— Select client —" }, ...investors.map(inv => ({ value: inv.id, label: investorLabel(inv) }))];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">{initial ? "Edit Sourced Deal" : "Add Sourced Deal"}</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <Select label="Property" value={f.property_id} onChange={set("property_id")} options={propOptions} />
        <Select label="Client" value={f.investor_id} onChange={set("investor_id")} options={clientOptions} />
        <Select label="Status" value={f.status} onChange={set("status")} options={SOURCED_STATUSES.map(s => ({ value: s.id, label: s.label }))} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Purchase Price" value={String(f.purchase_price)} onChange={set("purchase_price")} prefix="£" type="number" placeholder="250000" />
          <Field label="Sourcing Fee" value={String(f.sourcing_fee)} onChange={set("sourcing_fee")} prefix="£" type="number" placeholder="5000" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => set("fee_paid")(!f.fee_paid)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{ background: f.fee_paid ? "#4ade8022" : "#1f2135", color: f.fee_paid ? "#4ade80" : "#a2a2c0", border: `1px solid ${f.fee_paid ? "#4ade8044" : "#3e4060"}` }}
          >
            <span className={`w-3 h-3 rounded-full border-2 ${f.fee_paid ? "bg-[#4ade80] border-[#4ade80]" : "border-[#444]"}`} />
            Fee Paid
          </button>
          {f.fee_paid && <div className="flex-1"><Field label="Date Received" value={f.fee_paid_date} onChange={set("fee_paid_date")} type="date" /></div>}
        </div>
        <Field label="Notes" value={f.notes} onChange={set("notes")} placeholder="…" textarea />
        <GoldButton label={initial ? "Save Changes" : "Add Sourced Deal"} loadingLabel="Saving…" onClick={() => { onSave(f); onClose(); }} />
      </div>
    </div>
  );
}

// PortfolioModal
function PortfolioModal({ onSave, onClose, initialPort, properties = [], deals = [] }) {
  const editing = !!initialPort;
  const [port, setPort] = useState(initialPort || { property_id: "", purchase_price: "", current_valuation: "", mortgage_balance: "", mortgage_rate: "", mortgage_type: "repayment", monthly_mortgage: "", monthly_rent_actual: "", management_fee_pct: "", strategy: "btl", status: "active", acquisition_date: "" });
  const [selectedDealId, setSelectedDealId] = useState("");
  const so = (k) => (v) => setPort(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  const onDealSelect = (dealId) => {
    setSelectedDealId(dealId);
    if (!dealId) return;
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;
    setPort(p => ({ ...p, purchase_price: deal.offerPrice || deal.price || p.purchase_price }));
  };

  const propOptions = [{ value: "", label: "— Select a property —" }, ...properties.map(p => ({ value: p.id, label: `${p.address_line_1}${p.city ? `, ${p.city}` : ""}${p.postcode ? ` ${p.postcode}` : ""}` }))];
  const dealOptions = [{ value: "", label: "— None (enter manually) —" }, ...deals.filter(d => d.pipelineStatus !== "archived").map(d => ({ value: d.id, label: d.address }))];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">{editing ? "Edit Portfolio Entry" : "Add to Portfolio"}</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        {!editing && <>
          <Select label="Property *" value={port.property_id} onChange={so("property_id")} options={propOptions} />
          {properties.length === 0 && <p className="text-[11px] text-[#facc15]">No properties yet — add one in the Properties sub-tab first.</p>}
          <Select label="Pre-fill from Deal (optional)" value={selectedDealId} onChange={onDealSelect} options={dealOptions} />
        </>}
        <div className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0] pt-1">Financial Details</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Purchase Price *" value={String(port.purchase_price)} onChange={(v) => so("purchase_price")(Number(v))} prefix="£" placeholder="150000" type="number" />
          <Field label="Current Valuation" value={String(port.current_valuation)} onChange={(v) => so("current_valuation")(Number(v))} prefix="£" placeholder="180000" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mortgage Balance" value={String(port.mortgage_balance)} onChange={(v) => so("mortgage_balance")(Number(v))} prefix="£" placeholder="100000" type="number" />
          <Field label="Mortgage Rate" value={String(port.mortgage_rate)} onChange={(v) => so("mortgage_rate")(Number(v))} suffix="%" placeholder="3.5" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Mortgage Type" value={port.mortgage_type} onChange={so("mortgage_type")} options={["fixed","variable","interest_only","repayment"]} />
          <Field label="Monthly Mortgage" value={String(port.monthly_mortgage)} onChange={(v) => so("monthly_mortgage")(Number(v))} prefix="£" placeholder="500" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Monthly Rent" value={String(port.monthly_rent_actual)} onChange={(v) => so("monthly_rent_actual")(Number(v))} prefix="£" placeholder="800" type="number" />
          <Field label="Management Fee" value={String(port.management_fee_pct)} onChange={(v) => so("management_fee_pct")(Number(v))} suffix="%" placeholder="10" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Strategy" value={port.strategy} onChange={so("strategy")} options={["btl","hmo","sa","flip"]} />
          <Select label="Status" value={port.status} onChange={so("status")} options={["active","under_refurb","vacant","selling"]} />
        </div>
        <Field label="Acquisition Date" value={port.acquisition_date || ""} onChange={so("acquisition_date")} type="date" />
        <GoldButton label={editing ? "Save Changes" : "Add to Portfolio"} onClick={() => { onSave(port); onClose(); }} />
      </div>
    </div>
  );
}

// TenancyModal
function TenancyModal({ portfolioId, initial, onSave, onClose }) {
  const editing = !!initial;
  const [f, setF] = useState(editing ? {
    tenancy_type: initial.tenancy_type || "ast",
    start_date: initial.start_date || "",
    end_date: initial.end_date || "",
    monthly_rent: initial.monthly_rent || "",
    deposit_amount: initial.deposit_amount || "",
    deposit_scheme: initial.deposit_scheme || "DPS",
    status: initial.status || "active",
  } : { tenancy_type: "ast", start_date: "", end_date: "", monthly_rent: "", deposit_amount: "", deposit_scheme: "DPS", status: "active" });
  const [tenant, setTenant] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const sf = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  const st = (k) => (v) => setTenant(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">{editing ? "Edit Tenancy" : "Add Tenancy"}</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Type *" value={f.tenancy_type} onChange={sf("tenancy_type")} options={["ast","periodic","licence","company_let"]} />
          <Select label="Status" value={f.status} onChange={sf("status")} options={["active","ended","notice_served","renewed"]} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start Date *" value={f.start_date} onChange={sf("start_date")} type="date" />
          <Field label="End Date" value={f.end_date} onChange={sf("end_date")} type="date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Monthly Rent *" value={String(f.monthly_rent)} onChange={(v) => sf("monthly_rent")(Number(v))} prefix="£" type="number" />
          <Field label="Deposit" value={String(f.deposit_amount)} onChange={(v) => sf("deposit_amount")(Number(v))} prefix="£" type="number" />
        </div>
        <Select label="Deposit Scheme" value={f.deposit_scheme} onChange={sf("deposit_scheme")} options={["DPS","TDS","mydeposits"]} />
        {!editing && <>
          <div className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0] pt-2">Tenant (optional)</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name" value={tenant.first_name} onChange={st("first_name")} placeholder="John" />
            <Field label="Last Name" value={tenant.last_name} onChange={st("last_name")} placeholder="Doe" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={tenant.email} onChange={st("email")} type="email" />
            <Field label="Phone" value={tenant.phone} onChange={st("phone")} />
          </div>
        </>}
        <GoldButton label={editing ? "Save Changes" : "Add Tenancy"} onClick={() => { onSave(editing ? { tenancy: f, id: initial.id, portfolio_id: initial.portfolio_id } : { tenancy: { ...f, portfolio_id: portfolioId }, tenant }); onClose(); }} />
      </div>
    </div>
  );
}

// MaintenanceModal
function MaintenanceModal({ portfolioId, onSave, onClose }) {
  const [f, setF] = useState({ title: "", category: "general", priority: "medium", description: "", quoted_cost: "", status: "reported" });
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">Log Maintenance Issue</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <Field label="Title *" value={f.title} onChange={set("title")} placeholder="Boiler not working" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Category *" value={f.category} onChange={set("category")} options={["plumbing","electrical","structural","damp","heating","appliance","garden","general"]} />
          <Select label="Priority" value={f.priority} onChange={set("priority")} options={["emergency","high","medium","low"]} />
        </div>
        <Field label="Description" value={f.description} onChange={set("description")} placeholder="Details…" textarea />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Quoted Cost" value={String(f.quoted_cost)} onChange={(v) => set("quoted_cost")(Number(v))} prefix="£" type="number" />
          <Select label="Status" value={f.status} onChange={set("status")} options={["reported","quoted","approved","in_progress","completed","cancelled"]} />
        </div>
        <GoldButton label="Log Issue" onClick={() => { onSave({ ...f, portfolio_id: portfolioId }); onClose(); }} />
      </div>
    </div>
  );
}

// RentTrackerModal
function RentTrackerModal({ tenancy, onClose }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ due_date: "", amount_due: "", amount_paid: "", date_paid: "", payment_method: "bank_transfer", status: "paid" });
  const [editPayment, setEditPayment] = useState(null);
  const sf = (k) => (v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    supabase.from("rent_payments").select("*").eq("tenancy_id", tenancy.id).order("due_date", { ascending: false })
      .then(({ data }) => { setPayments(data || []); setLoading(false); });
  }, [tenancy.id]);

  const logPayment = async () => {
    const { data: row } = await supabase.from("rent_payments").insert({ ...form, tenancy_id: tenancy.id, amount_due: Number(form.amount_due), amount_paid: Number(form.amount_paid) }).select().single();
    if (row) setPayments(p => [row, ...p]);
    setForm({ due_date: "", amount_due: "", amount_paid: "", date_paid: "", payment_method: "bank_transfer", status: "paid" });
  };

  const updatePayment = async (id, data) => {
    const { data: row } = await supabase.from("rent_payments").update(data).eq("id", id).select().single();
    if (row) setPayments(ps => ps.map(p => p.id === id ? row : p));
    setEditPayment(null);
  };

  const deletePayment = async (id) => {
    await supabase.from("rent_payments").delete().eq("id", id);
    setPayments(ps => ps.filter(p => p.id !== id));
  };

  const totalDue = payments.reduce((s, p) => s + (p.amount_due || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + (p.amount_paid || 0), 0);
  const arrears = totalDue - totalPaid;

  const STATUS_P = { paid: "#4ade80", partial: "#facc15", late: "#fb923c", missed: "#f87171", pending: "#a2a2c0" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-2xl shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">Rent Tracker</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Total Received" value={fmt(totalPaid)} accent />
          <StatCard label="Total Due" value={fmt(totalDue)} />
          <StatCard label="Arrears" value={fmt(arrears)} sub={arrears > 0 ? "Outstanding" : "Clear"} />
        </div>
        {loading ? <div className="text-xs text-[#a2a2c0]">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-[#a2a2c0]">
              <thead><tr className="border-b border-[#373954] text-[9px] uppercase tracking-wider text-[#62647c]">
                <th className="text-left py-2">Due Date</th><th className="text-right py-2">Amount Due</th><th className="text-right py-2">Paid</th><th className="text-left py-2 pl-3">Status</th><th className="py-2" />
              </tr></thead>
              <tbody className="divide-y divide-[#373954]">
                {payments.map(p => editPayment?.id === p.id ? (
                  <tr key={p.id} className="bg-[#1f2135]">
                    <td colSpan={5} className="py-2 px-1">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <Field label="Amount Paid" value={String(editPayment.amount_paid)} onChange={(v) => setEditPayment(ep => ({ ...ep, amount_paid: v }))} prefix="£" type="number" />
                        <Select label="Status" value={editPayment.status} onChange={(v) => setEditPayment(ep => ({ ...ep, status: v }))} options={["paid","partial","late","missed","pending"]} />
                        <Field label="Date Paid" value={editPayment.date_paid || ""} onChange={(v) => setEditPayment(ep => ({ ...ep, date_paid: v }))} type="date" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => updatePayment(p.id, { amount_paid: Number(editPayment.amount_paid), status: editPayment.status, date_paid: editPayment.date_paid })} className="text-[10px] font-bold text-[#4ade80] border border-[#4ade8033] px-3 py-1 rounded">Save</button>
                        <button onClick={() => setEditPayment(null)} className="text-[10px] text-[#62647c] px-3 py-1 rounded">Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={p.id}>
                    <td className="py-2">{p.due_date}</td>
                    <td className="text-right">{fmt(p.amount_due)}</td>
                    <td className="text-right">{fmt(p.amount_paid)}</td>
                    <td className="pl-3"><span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: (STATUS_P[p.status] || "#a2a2c0") + "22", color: STATUS_P[p.status] || "#a2a2c0" }}>{p.status}</span></td>
                    <td className="text-right pl-2">
                      <button onClick={() => setEditPayment(p)} className="text-[#62647c] hover:text-[#c9a227] text-xs mr-1">✏️</button>
                      <button onClick={() => deletePayment(p.id)} className="text-[#62647c] hover:text-[#f87171] text-xs">✕</button>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-[#505270]">No payments logged</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-[#373954] pt-4 space-y-3">
          <div className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Log Payment</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due Date *" value={form.due_date} onChange={sf("due_date")} type="date" />
            <Field label="Date Paid" value={form.date_paid} onChange={sf("date_paid")} type="date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount Due *" value={String(form.amount_due)} onChange={(v) => sf("amount_due")(v)} prefix="£" type="number" />
            <Field label="Amount Paid" value={String(form.amount_paid)} onChange={(v) => sf("amount_paid")(v)} prefix="£" type="number" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Method" value={form.payment_method} onChange={sf("payment_method")} options={["bank_transfer","standing_order","cash","cheque"]} />
            <Select label="Status" value={form.status} onChange={sf("status")} options={["paid","partial","late","missed","pending"]} />
          </div>
          <GoldButton label="Log Payment" onClick={logPayment} />
        </div>
      </div>
    </div>
  );
}

// PropertyModal
function PropertyModal({ initial, onSave, onClose }) {
  const blank = { address_line_1: "", address_line_2: "", city: "", county: "", postcode: "", property_type: "", bedrooms: "", bathrooms: "", sq_ft: "", tenure: "", lease_years_remaining: "", epc_rating: "", epc_expiry: "", council_tax_band: "", flood_risk: "", title_number: "", listing_url: "", notes: "" };
  const [f, setF] = useState({ ...blank, ...(initial ? Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v === null || v === undefined ? "" : v])) : {}) });
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">{initial ? "Edit Property" : "Add Property"}</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        <PostcodeLookup
          postcode={f.postcode}
          onPostcodeChange={set("postcode")}
          onSelect={(a) => setF(p => ({ ...p, address_line_1: a.line1, address_line_2: a.line2, city: a.city, county: a.county }))}
        />
        <Field label="Address Line 1 *" value={f.address_line_1} onChange={set("address_line_1")} placeholder="12 High Street" />
        <Field label="Address Line 2" value={f.address_line_2} onChange={set("address_line_2")} placeholder="Flat 3" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="City" value={f.city} onChange={set("city")} placeholder="Birmingham" />
          <Field label="County" value={f.county} onChange={set("county")} placeholder="West Midlands" />
        </div>
        <Select label="Property Type" value={f.property_type} onChange={set("property_type")} options={[{ value: "", label: "— select —" }, "terraced", "semi_detached", "detached", "flat", "bungalow", "hmo", "commercial", "land", "new_build"]} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Bedrooms" value={String(f.bedrooms)} onChange={set("bedrooms")} placeholder="3" type="number" />
          <Field label="Bathrooms" value={String(f.bathrooms)} onChange={set("bathrooms")} placeholder="1" type="number" />
          <Field label="Sq Ft" value={String(f.sq_ft)} onChange={set("sq_ft")} placeholder="900" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Tenure" value={f.tenure} onChange={set("tenure")} options={[{ value: "", label: "— select —" }, "freehold", "leasehold", "share_of_freehold"]} />
          <Field label="Lease Years Remaining" value={String(f.lease_years_remaining)} onChange={set("lease_years_remaining")} placeholder="125" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="EPC Rating" value={f.epc_rating} onChange={set("epc_rating")} options={[{ value: "", label: "— select —" }, "A", "B", "C", "D", "E", "F", "G"]} />
          <Field label="EPC Expiry" value={f.epc_expiry} onChange={set("epc_expiry")} type="date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Council Tax Band" value={f.council_tax_band} onChange={set("council_tax_band")} options={[{ value: "", label: "— select —" }, "A", "B", "C", "D", "E", "F", "G", "H"]} />
          <Select label="Flood Risk" value={f.flood_risk} onChange={set("flood_risk")} options={[{ value: "", label: "— select —" }, "very_low", "low", "medium", "high"]} />
        </div>
        <Field label="Title Number" value={f.title_number} onChange={set("title_number")} placeholder="ABS12345" />
        <Field label="Listing URL" value={f.listing_url} onChange={set("listing_url")} placeholder="https://rightmove.co.uk/..." />
        <Field label="Notes" value={f.notes} onChange={set("notes")} placeholder="…" textarea />
        <GoldButton label={initial ? "Save Changes" : "Add Property"} onClick={() => { onSave(f); onClose(); }} />
      </div>
    </div>
  );
}

// PortfolioTab
function PortfolioTab({ portfolio, userId, onAdd, onDelete, onUpdate, investors = [], contacts = [], deals = [] }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editPortfolio, setEditPortfolio] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [tenancies, setTenancies] = useState({});
  const [maintenance, setMaintenance] = useState({});
  const [showTenancy, setShowTenancy] = useState(null);
  const [showEditTenancy, setShowEditTenancy] = useState(null);
  const [showMaintenance, setShowMaintenance] = useState(null);
  const [showRent, setShowRent] = useState(null);
  const [subTab, setSubTab] = useState("portfolio");
  const [properties, setProperties] = useState([]);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [showAddProp, setShowAddProp] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [sourced, setSourced] = useState([]);
  const [sourcedLoaded, setSourcedLoaded] = useState(false);
  const [showAddSourced, setShowAddSourced] = useState(false);
  const [editSourced, setEditSourced] = useState(null);

  const STATUS_COLORS = { active: "#4ade80", under_refurb: "#facc15", vacant: "#f87171", selling: "#fb923c", sold: "#a2a2c0" };
  const STRAT_COLORS = { btl: "#60a5fa", hmo: "#f472b6", sa: "#34d399", flip: "#facc15" };
  const PRIORITY_COLORS = { emergency: "#f87171", high: "#fb923c", medium: "#facc15", low: "#4ade80" };

  const loadTenancies = async (portId) => {
    if (tenancies[portId]) return;
    const { data: tens } = await supabase.from("tenancies").select("*, tenants(first_name, last_name)").eq("portfolio_id", portId).order("start_date", { ascending: false });
    setTenancies(p => ({ ...p, [portId]: tens || [] }));
  };

  const loadMaintenance = async (portId) => {
    if (maintenance[portId]) return;
    const { data: issues } = await supabase.from("maintenance_requests").select("*").eq("portfolio_id", portId).order("created_at", { ascending: false });
    setMaintenance(p => ({ ...p, [portId]: issues || [] }));
  };

  const handleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    loadTenancies(id);
    loadMaintenance(id);
  };

  const saveTenancy = async ({ tenancy, tenant }) => {
    // Strip empty strings to null so optional DATE/NUMERIC columns don't reject
    const clean = Object.fromEntries(Object.entries(tenancy).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: ten, error } = await supabase.from("tenancies").insert(clean).select().single();
    if (error) { console.error("saveTenancy error:", error); alert("Failed to save tenancy: " + error.message); return; }
    if (ten) {
      setTenancies(p => ({ ...p, [tenancy.portfolio_id]: [ten, ...(p[tenancy.portfolio_id] || [])] }));
      if (tenant?.first_name) {
        const { data: t } = await supabase.from("tenants").insert({ ...tenant, tenancy_id: ten.id }).select().single();
        if (t) setTenancies(p => ({ ...p, [tenancy.portfolio_id]: (p[tenancy.portfolio_id] || []).map(x => x.id === ten.id ? { ...x, tenants: [t] } : x) }));
      }
    }
  };

  const updateTenancy = async ({ tenancy, id, portfolio_id }) => {
    const clean = Object.fromEntries(Object.entries(tenancy).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: row, error } = await supabase.from("tenancies").update(clean).eq("id", id).select("*, tenants(first_name, last_name)").single();
    if (error) { console.error("updateTenancy error:", error); alert("Failed to update tenancy: " + error.message); return; }
    if (row) setTenancies(p => ({ ...p, [portfolio_id]: (p[portfolio_id] || []).map(x => x.id === id ? row : x) }));
  };

  const deleteTenancy = async (portId, id) => {
    await supabase.from("tenancies").delete().eq("id", id);
    setTenancies(p => ({ ...p, [portId]: (p[portId] || []).filter(x => x.id !== id) }));
  };

  const saveMaintenance = async (data) => {
    const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: row } = await supabase.from("maintenance_requests").insert(clean).select().single();
    if (row) setMaintenance(p => ({ ...p, [data.portfolio_id]: [row, ...(p[data.portfolio_id] || [])] }));
  };

  const updateMaintenance = async (portId, id, data) => {
    const { data: row } = await supabase.from("maintenance_requests").update(data).eq("id", id).select().single();
    if (row) setMaintenance(p => ({ ...p, [portId]: (p[portId] || []).map(x => x.id === id ? row : x) }));
  };

  const deleteMaintenance = async (portId, id) => {
    await supabase.from("maintenance_requests").delete().eq("id", id);
    setMaintenance(p => ({ ...p, [portId]: (p[portId] || []).filter(x => x.id !== id) }));
  };

  const fetchProperties = async () => {
    const { data } = await supabase.from("properties").select("*").eq("user_id", userId).eq("archived", false).order("created_at", { ascending: false });
    setProperties(data || []);
    setPropertiesLoaded(true);
  };

  const addProperty = async (data) => {
    const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: row, error } = await supabase.from("properties").insert({ ...clean, user_id: userId }).select().single();
    if (error) { alert("Failed to save property: " + error.message); return; }
    if (row) setProperties(prev => [row, ...prev]);
  };

  const updateProperty = async (id, data) => {
    const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: row } = await supabase.from("properties").update(clean).eq("id", id).select().single();
    if (row) setProperties(prev => prev.map(p => p.id === id ? row : p));
  };

  const deleteProperty = async (id) => {
    const { error } = await supabase.from("properties").delete().eq("id", id);
    if (error) { alert("Cannot delete: this property is linked to a portfolio entry."); return; }
    setProperties(prev => prev.filter(p => p.id !== id));
  };

  const fetchSourced = async () => {
    const { data } = await supabase.from("sourced_properties")
      .select("*, properties(address_line_1, city, postcode, property_type), investors(id, contact_id)")
      .eq("user_id", userId).order("created_at", { ascending: false });
    setSourced(data || []);
    setSourcedLoaded(true);
  };

  const addSourced = async (data) => {
    const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: row } = await supabase.from("sourced_properties")
      .insert({ ...clean, user_id: userId })
      .select("*, properties(address_line_1, city, postcode, property_type), investors(id, contact_id)")
      .single();
    if (row) setSourced(prev => [row, ...prev]);
  };

  const updateSourced = async (id, data) => {
    const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: row } = await supabase.from("sourced_properties")
      .update(clean).eq("id", id)
      .select("*, properties(address_line_1, city, postcode, property_type), investors(id, contact_id)")
      .single();
    if (row) setSourced(prev => prev.map(s => s.id === id ? row : s));
  };

  const deleteSourced = async (id) => {
    setSourced(prev => prev.filter(s => s.id !== id));
    await supabase.from("sourced_properties").delete().eq("id", id);
  };

  const sourcedInvestorName = (s) => {
    const inv = s.investors;
    if (!inv) return "—";
    const c = contacts.find(x => x.id === inv.contact_id);
    return c ? `${c.first_name} ${c.last_name}${c.company_name ? ` — ${c.company_name}` : ""}` : "—";
  };

  const active = portfolio.filter(p => p.status === "active");
  const totalEquity = portfolio.reduce((s, p) => s + ((p.current_valuation || 0) - (p.mortgage_balance || 0)), 0);
  const monthlyRent = active.reduce((s, p) => s + (p.monthly_rent_actual || 0), 0);
  const monthlyCashflow = active.reduce((s, p) => s + ((p.monthly_rent_actual || 0) - (p.monthly_mortgage || 0)), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#f5f4ff]">
          {subTab === "portfolio" && <>Portfolio <span className="text-[#a2a2c0] text-sm font-normal">({portfolio.length})</span></>}
          {subTab === "properties" && <>Properties <span className="text-[#a2a2c0] text-sm font-normal">({properties.length})</span></>}
          {subTab === "sourced" && <>Sourced <span className="text-[#a2a2c0] text-sm font-normal">({sourced.length})</span></>}
        </h2>
        {subTab === "portfolio" && <button onClick={() => { if (!propertiesLoaded) fetchProperties(); setShowAdd(true); }} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#171829] transition-all" style={{ background: "linear-gradient(135deg,#c9a227,#d4af37)" }}>+ Add Entry</button>}
        {subTab === "properties" && <button onClick={() => setShowAddProp(true)} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#171829] transition-all" style={{ background: "linear-gradient(135deg,#c9a227,#d4af37)" }}>+ Add Property</button>}
        {subTab === "sourced" && <button onClick={() => { if (!propertiesLoaded) fetchProperties(); setShowAddSourced(true); }} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#171829] transition-all" style={{ background: "linear-gradient(135deg,#c9a227,#d4af37)" }}>+ Add Sourced</button>}
      </div>

      <div className="flex gap-1 p-1 bg-[#1a1b2e] rounded-xl w-fit">
        {[["portfolio", "🏘 Portfolio"], ["properties", "🏠 Properties"], ["sourced", "🤝 Sourced"]].map(([t, label]) => (
          <button key={t} onClick={() => { setSubTab(t); if (t === "properties" && !propertiesLoaded) fetchProperties(); if (t === "sourced" && !sourcedLoaded) { fetchSourced(); if (!propertiesLoaded) fetchProperties(); } }}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider uppercase transition-all ${subTab === t ? "text-[#171829]" : "text-[#6a6a82] hover:text-[#e0dfe4]"}`}
            style={subTab === t ? { background: "linear-gradient(135deg,#c9a227,#d4af37)" } : {}}>
            {label}
          </button>
        ))}
      </div>

      {subTab === "portfolio" && (<>
        {portfolio.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Properties" value={portfolio.length} />
            <StatCard label="Total Equity" value={fmt(totalEquity)} accent />
            <StatCard label="Monthly Rent" value={fmt(monthlyRent)} />
            <StatCard label="Monthly Cashflow" value={fmt(monthlyCashflow)} sub={monthlyCashflow >= 0 ? "Positive" : "Negative"} />
          </div>
        )}

        {portfolio.length === 0 && (
          <div className="text-center py-16 space-y-2">
            <div className="text-4xl">🏘</div>
            <div className="text-sm font-semibold text-[#62647c]">No portfolio entries yet</div>
            <div className="text-xs text-[#505270]">Add your first buy-to-let or investment property</div>
          </div>
        )}

      <div className="space-y-2">
        {portfolio.map(p => {
          const addr = p.properties ? `${p.properties.address_line_1}, ${p.properties.city}` : "Unknown address";
          const cashflow = (p.monthly_rent_actual || 0) - (p.monthly_mortgage || 0);
          const equity = (p.current_valuation || 0) - (p.mortgage_balance || 0);
          return (
            <div key={p.id} className="bg-[#252737] border border-[#373954] rounded-xl overflow-hidden">
              <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#2b2d42] transition-colors" onClick={() => handleExpand(p.id)}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0 bg-[#c9a22722]">🏠</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#f5f4ff] truncate">{addr}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {p.strategy && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase" style={{ background: (STRAT_COLORS[p.strategy] || "#a2a2c0") + "22", color: STRAT_COLORS[p.strategy] || "#a2a2c0" }}>{p.strategy}</span>}
                    {p.status && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase" style={{ background: (STATUS_COLORS[p.status] || "#a2a2c0") + "22", color: STATUS_COLORS[p.status] || "#a2a2c0" }}>{p.status}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold" style={{ color: cashflow >= 0 ? "#4ade80" : "#f87171" }}>{fmt(cashflow)}/mo</div>
                  <div className="text-[10px] text-[#787898]">Equity: {fmt(equity)}</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setEditPortfolio(p); }} className="text-[#62647c] hover:text-[#c9a227] transition-colors text-sm p-1 ml-1">✏️</button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} className="text-[#62647c] hover:text-[#f87171] transition-colors text-sm p-1 ml-1">✕</button>
              </div>
              {expandedId === p.id && (
                <div className="border-t border-[#373954] p-4 space-y-5">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-[#a2a2c0]">
                    <div><div className="text-[9px] uppercase text-[#62647c] mb-0.5">Purchase Price</div><div className="font-semibold text-[#f5f4ff]">{fmt(p.purchase_price)}</div></div>
                    <div><div className="text-[9px] uppercase text-[#62647c] mb-0.5">Current Value</div><div className="font-semibold text-[#f5f4ff]">{fmt(p.current_valuation)}</div></div>
                    <div><div className="text-[9px] uppercase text-[#62647c] mb-0.5">Mortgage</div><div className="font-semibold text-[#f5f4ff]">{fmt(p.mortgage_balance)} @ {p.mortgage_rate}%</div></div>
                    <div><div className="text-[9px] uppercase text-[#62647c] mb-0.5">Monthly Rent</div><div className="font-semibold text-[#4ade80]">{fmt(p.monthly_rent_actual)}</div></div>
                    <div><div className="text-[9px] uppercase text-[#62647c] mb-0.5">Mth Mortgage</div><div className="font-semibold text-[#f87171]">{fmt(p.monthly_mortgage)}</div></div>
                    <div><div className="text-[9px] uppercase text-[#62647c] mb-0.5">Cashflow</div><div className="font-semibold" style={{ color: cashflow >= 0 ? "#4ade80" : "#f87171" }}>{fmt(cashflow)}</div></div>
                  </div>

                  {/* Tenancies */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Tenancies</span>
                      <button onClick={() => setShowTenancy(p.id)} className="text-[10px] font-bold text-[#c9a227] hover:text-[#d4af37]">+ Add Tenancy</button>
                    </div>
                    {(tenancies[p.id] || []).length === 0 && <div className="text-xs text-[#505270]">No tenancies</div>}
                    <div className="space-y-2">
                      {(tenancies[p.id] || []).map(t => (
                        <div key={t.id} className="bg-[#1f2135] rounded-lg p-3 flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[9px] font-bold text-[#c9a227] uppercase">{t.tenancy_type}</span>
                              <span className="text-xs text-[#f5f4ff]">{fmt(t.monthly_rent)}/mo</span>
                              <span className="text-[9px] text-[#787898]">{t.start_date} – {t.end_date || "ongoing"}</span>
                            </div>
                            {(t.tenants || []).length > 0 && (
                              <div className="text-[10px] text-[#a2a2c0] mt-1">{t.tenants.map(x => `${x.first_name} ${x.last_name}`).join(", ")}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => setShowRent(t)} className="text-[10px] font-bold text-[#60a5fa] hover:text-[#93c5fd] border border-[#60a5fa33] px-2 py-1 rounded">Rent</button>
                            <button onClick={() => setShowEditTenancy(t)} className="text-[#62647c] hover:text-[#c9a227] transition-colors text-xs p-1">✏️</button>
                            <button onClick={() => deleteTenancy(p.id, t.id)} className="text-[#62647c] hover:text-[#f87171] transition-colors text-xs p-1">✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Maintenance */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Maintenance</span>
                      <button onClick={() => setShowMaintenance(p.id)} className="text-[10px] font-bold text-[#c9a227] hover:text-[#d4af37]">+ Log Issue</button>
                    </div>
                    {(maintenance[p.id] || []).length === 0 && <div className="text-xs text-[#505270]">No maintenance issues</div>}
                    <div className="space-y-1.5">
                      {(maintenance[p.id] || []).map(m => (
                        <div key={m.id} className="bg-[#1f2135] rounded-lg p-2.5 flex items-start gap-2">
                          <div className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ background: PRIORITY_COLORS[m.priority] || "#a2a2c0" }} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-[#f5f4ff]">{m.title}</span>
                              {m.quoted_cost ? <span className="text-[9px] text-[#a2a2c0]">{fmt(m.quoted_cost)}</span> : null}
                              {m.actual_cost ? <span className="text-[9px] text-[#4ade80]">actual: {fmt(m.actual_cost)}</span> : null}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <select
                                value={m.status}
                                onChange={(e) => updateMaintenance(p.id, m.id, { status: e.target.value })}
                                className="text-[9px] bg-[#252737] border border-[#373954] rounded px-1 py-0.5 text-[#a2a2c0] focus:outline-none focus:border-[#c9a227]"
                              >
                                {["reported","quoted","approved","in_progress","completed","cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                              <input
                                type="number"
                                placeholder="Actual cost £"
                                defaultValue={m.actual_cost || ""}
                                onBlur={(e) => { if (e.target.value) updateMaintenance(p.id, m.id, { actual_cost: Number(e.target.value) }); }}
                                className="text-[9px] bg-[#252737] border border-[#373954] rounded px-1.5 py-0.5 text-[#a2a2c0] w-24 focus:outline-none focus:border-[#c9a227]"
                              />
                              <span className="text-[9px] text-[#62647c]">{m.category} · {m.priority}</span>
                            </div>
                          </div>
                          <button onClick={() => deleteMaintenance(p.id, m.id)} className="text-[#62647c] hover:text-[#f87171] transition-colors text-xs shrink-0">✕</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showAdd && <PortfolioModal onSave={onAdd} onClose={() => setShowAdd(false)} properties={properties} deals={deals} />}
      {editPortfolio && (
        <PortfolioModal
          initialPort={{ purchase_price: editPortfolio.purchase_price || "", current_valuation: editPortfolio.current_valuation || "", mortgage_balance: editPortfolio.mortgage_balance || "", mortgage_rate: editPortfolio.mortgage_rate || "", mortgage_type: editPortfolio.mortgage_type || "repayment", monthly_mortgage: editPortfolio.monthly_mortgage || "", monthly_rent_actual: editPortfolio.monthly_rent_actual || "", management_fee_pct: editPortfolio.management_fee_pct || "", strategy: editPortfolio.strategy || "btl", status: editPortfolio.status || "active", acquisition_date: editPortfolio.acquisition_date || "" }}
          onSave={(portData) => { onUpdate(editPortfolio.id, portData); setEditPortfolio(null); }}
          onClose={() => setEditPortfolio(null)}
          properties={properties}
          deals={deals}
        />
      )}
      {showTenancy && <TenancyModal portfolioId={showTenancy} onSave={saveTenancy} onClose={() => setShowTenancy(null)} />}
      {showEditTenancy && <TenancyModal portfolioId={showEditTenancy.portfolio_id} initial={showEditTenancy} onSave={updateTenancy} onClose={() => setShowEditTenancy(null)} />}
      {showMaintenance && <MaintenanceModal portfolioId={showMaintenance} onSave={saveMaintenance} onClose={() => setShowMaintenance(null)} />}
      {showRent && <RentTrackerModal tenancy={showRent} onClose={() => setShowRent(null)} />}
      </>)}

      {subTab === "properties" && (
        <>
          {properties.length === 0 && (
            <div className="text-center py-16 space-y-2">
              <div className="text-4xl">🏠</div>
              <div className="text-sm font-semibold text-[#62647c]">No property records yet</div>
              <div className="text-xs text-[#505270]">Add standalone property records to your database</div>
            </div>
          )}
          <div className="space-y-2">
            {properties.map(p => (
              <div key={p.id} className="bg-[#252737] border border-[#373954] rounded-xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0 bg-[#c9a22722]">🏠</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#f5f4ff]">{p.address_line_1}{p.city ? `, ${p.city}` : ""}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {p.postcode && <span className="text-[10px] text-[#787898]">{p.postcode}</span>}
                    {p.property_type && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#c9a22722] text-[#c9a227] uppercase">{p.property_type.replace(/_/g, " ")}</span>}
                    {p.bedrooms && <span className="text-[10px] text-[#787898]">{p.bedrooms} bed</span>}
                    {p.epc_rating && <span className="text-[10px] text-[#787898]">EPC: {p.epc_rating}</span>}
                    {p.tenure && <span className="text-[10px] text-[#787898] capitalize">{p.tenure.replace(/_/g, " ")}</span>}
                  </div>
                </div>
                <button onClick={() => setEditProp(p)} className="text-[#62647c] hover:text-[#c9a227] transition-colors text-sm p-1">✏️</button>
                <button onClick={() => deleteProperty(p.id)} className="text-[#62647c] hover:text-[#f87171] transition-colors text-sm p-1">✕</button>
              </div>
            ))}
          </div>
        </>
      )}

      {showAddProp && <PropertyModal onSave={async (data) => { await addProperty(data); setShowAddProp(false); }} onClose={() => setShowAddProp(false)} />}
      {editProp && <PropertyModal initial={editProp} onSave={(data) => { updateProperty(editProp.id, data); setEditProp(null); }} onClose={() => setEditProp(null)} />}

      {subTab === "sourced" && (() => {
        const totalFees = sourced.reduce((s, x) => s + (Number(x.sourcing_fee) || 0), 0);
        const outstanding = sourced.filter(x => !x.fee_paid).reduce((s, x) => s + (Number(x.sourcing_fee) || 0), 0);
        const completed = sourced.filter(x => x.status === "completed").length;
        return (
          <>
            {sourced.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Deals Sourced" value={sourced.length} />
                <StatCard label="Completed" value={completed} accent />
                <StatCard label="Total Fees" value={fmt(totalFees)} />
                <StatCard label="Outstanding" value={fmt(outstanding)} sub={outstanding > 0 ? "Unpaid" : "All paid"} />
              </div>
            )}
            {sourced.length === 0 && (
              <div className="text-center py-16 space-y-2">
                <div className="text-4xl">🤝</div>
                <div className="text-sm font-semibold text-[#62647c]">No sourced deals yet</div>
                <div className="text-xs text-[#505270]">Record properties you have found for investor clients</div>
              </div>
            )}
            <div className="space-y-2">
              {sourced.map(s => {
                const statusObj = SOURCED_STATUSES.find(x => x.id === s.status) || SOURCED_STATUSES[0];
                const propAddr = s.properties ? `${s.properties.address_line_1}${s.properties.city ? `, ${s.properties.city}` : ""}` : "—";
                const clientName = sourcedInvestorName(s);
                return (
                  <div key={s.id} className="bg-[#252737] border border-[#373954] rounded-xl p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0 bg-[#34d39922]">🤝</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#f5f4ff] truncate">{propAddr}</div>
                      <div className="text-xs text-[#a2a2c0] mt-0.5 truncate">{clientName}</div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase" style={{ background: statusObj.color + "22", color: statusObj.color }}>{statusObj.label}</span>
                        {s.purchase_price && <span className="text-[10px] text-[#787898]">{fmt(s.purchase_price)}</span>}
                        {s.sourcing_fee && (
                          <span className="text-[10px] font-semibold" style={{ color: s.fee_paid ? "#4ade80" : "#facc15" }}>
                            {fmt(s.sourcing_fee)} {s.fee_paid ? "✓ paid" : "· unpaid"}
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => { if (!propertiesLoaded) fetchProperties(); setEditSourced(s); }} className="text-[#62647c] hover:text-[#c9a227] transition-colors text-sm p-1">✏️</button>
                    <button onClick={() => { if (window.confirm("Delete this sourced deal?")) deleteSourced(s.id); }} className="text-[#62647c] hover:text-[#f87171] transition-colors text-sm p-1">✕</button>
                  </div>
                );
              })}
            </div>
            {showAddSourced && <SourcedModal onSave={addSourced} onClose={() => setShowAddSourced(false)} properties={properties} investors={investors} contacts={contacts} />}
            {editSourced && <SourcedModal initial={editSourced} onSave={(data) => { updateSourced(editSourced.id, data); setEditSourced(null); }} onClose={() => setEditSourced(null)} properties={properties} investors={investors} contacts={contacts} />}
          </>
        );
      })()}
    </div>
  );
}

// InvestorModal
function InvestorModal({ onSave, onClose, initial, initialContact }) {
  const editing = !!initial;
  const [contact, setContact] = useState(initialContact || { first_name: "", last_name: "", company_name: "", email: "", phone: "" });
  const [inv, setInv] = useState(initial ? {
    total_capital_available: initial.total_capital_available || "",
    capital_deployed: initial.capital_deployed || "",
    risk_appetite: initial.risk_appetite || "moderate",
    target_return: initial.target_return || "",
    investor_status: initial.investor_status || "active",
    notes: initial.notes || "",
  } : { total_capital_available: "", capital_deployed: "", risk_appetite: "moderate", target_return: "", investor_status: "active", notes: "" });
  const sc = (k) => (v) => setContact(p => ({ ...p, [k]: v }));
  const si = (k) => (v) => setInv(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#c9a227] tracking-wider uppercase">{editing ? "Edit Investor" : "Add Investor"}</h3>
          <button onClick={onClose} className="text-[#62647c] hover:text-[#f87171] text-lg">✕</button>
        </div>
        {!editing && <>
          <div className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0]">Contact Details</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name" value={contact.first_name} onChange={sc("first_name")} placeholder="James" />
            <Field label="Last Name" value={contact.last_name} onChange={sc("last_name")} placeholder="Wilson" />
          </div>
          <Field label="Company" value={contact.company_name} onChange={sc("company_name")} placeholder="Wilson Capital" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={contact.email} onChange={sc("email")} type="email" />
            <Field label="Phone" value={contact.phone} onChange={sc("phone")} />
          </div>
        </>}
        <div className="text-[10px] font-bold tracking-[.15em] uppercase text-[#a2a2c0] pt-2">Investment Profile</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Capital Available" value={String(inv.total_capital_available)} onChange={(v) => si("total_capital_available")(Number(v))} prefix="£" type="number" />
          <Field label="Capital Deployed" value={String(inv.capital_deployed)} onChange={(v) => si("capital_deployed")(Number(v))} prefix="£" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Risk Appetite" value={inv.risk_appetite} onChange={si("risk_appetite")} options={["conservative","moderate","aggressive"]} />
          <Field label="Target Return %" value={String(inv.target_return)} onChange={(v) => si("target_return")(Number(v))} suffix="%" type="number" />
        </div>
        <Select label="Status" value={inv.investor_status} onChange={si("investor_status")} options={["active","paused","exited"]} />
        <Field label="Notes" value={inv.notes} onChange={si("notes")} placeholder="…" textarea />
        <GoldButton label={editing ? "Save Changes" : "Add Investor"} onClick={() => { onSave(contact, inv); onClose(); }} />
      </div>
    </div>
  );
}

// InvestorsTab
function InvestorsTab({ investors, contacts, onAdd, onDelete, onUpdate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editInvestor, setEditInvestor] = useState(null);
  const RISK_COLORS = { conservative: "#60a5fa", moderate: "#facc15", aggressive: "#f87171" };
  const STATUS_I = { active: "#4ade80", paused: "#facc15", exited: "#a2a2c0" };

  const totalCapital = investors.reduce((s, i) => s + (i.total_capital_available || 0), 0);
  const totalDeployed = investors.reduce((s, i) => s + (i.capital_deployed || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#f5f4ff]">Investors <span className="text-[#a2a2c0] text-sm font-normal">({investors.length})</span></h2>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider uppercase text-[#171829] transition-all" style={{ background: "linear-gradient(135deg,#c9a227,#d4af37)" }}>+ Add Investor</button>
      </div>

      {investors.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Total Investors" value={investors.length} />
          <StatCard label="Capital Available" value={fmt(totalCapital)} accent />
          <StatCard label="Deployed" value={fmt(totalDeployed)} sub={totalCapital ? pct(totalDeployed / totalCapital * 100) + " deployed" : undefined} />
        </div>
      )}

      {investors.length === 0 && (
        <div className="text-center py-16 space-y-2">
          <div className="text-4xl">💼</div>
          <div className="text-sm font-semibold text-[#62647c]">No investors yet</div>
          <div className="text-xs text-[#505270]">Add angel investors, JV partners and private lenders</div>
        </div>
      )}

      <div className="space-y-2">
        {investors.map(inv => {
          const contact = contacts.find(c => c.id === inv.contact_id);
          const name = contact ? `${contact.first_name} ${contact.last_name}` : "Unknown";
          const company = contact?.company_name;
          const deployedPct = inv.total_capital_available ? (inv.capital_deployed || 0) / inv.total_capital_available : 0;
          return (
            <div key={inv.id} className="bg-[#252737] border border-[#373954] rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: (RISK_COLORS[inv.risk_appetite] || "#a2a2c0") + "33", color: RISK_COLORS[inv.risk_appetite] || "#a2a2c0" }}>
                    {(contact?.first_name?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#f5f4ff]">{name}</div>
                    {company && <div className="text-xs text-[#a2a2c0]">{company}</div>}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {inv.risk_appetite && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: (RISK_COLORS[inv.risk_appetite] || "#a2a2c0") + "22", color: RISK_COLORS[inv.risk_appetite] || "#a2a2c0" }}>{inv.risk_appetite}</span>}
                      {inv.investor_status && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: (STATUS_I[inv.investor_status] || "#a2a2c0") + "22", color: STATUS_I[inv.investor_status] || "#a2a2c0" }}>{inv.investor_status}</span>}
                      {inv.target_return && <span className="text-[9px] text-[#a2a2c0]">Target: {inv.target_return}%</span>}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-[#c9a227]">{fmt(inv.total_capital_available)}</div>
                  <div className="text-[10px] text-[#787898]">{fmt(inv.capital_deployed)} deployed</div>
                  <div className="flex items-center gap-1 justify-end mt-1">
                    <button onClick={() => setEditInvestor(inv)} className="text-[#62647c] hover:text-[#c9a227] transition-colors text-sm">✏️</button>
                    <button onClick={() => onDelete(inv.id)} className="text-[#62647c] hover:text-[#f87171] transition-colors text-sm">✕</button>
                  </div>
                </div>
              </div>
              {inv.total_capital_available > 0 && (
                <div className="mt-3">
                  <div className="flex justify-between text-[9px] text-[#62647c] mb-1">
                    <span>Deployed</span><span>{pct(deployedPct * 100)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#1f2135] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(deployedPct * 100, 100)}%`, background: RISK_COLORS[inv.risk_appetite] || "#c9a227" }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showAdd && <InvestorModal onSave={onAdd} onClose={() => setShowAdd(false)} />}
      {editInvestor && (
        <InvestorModal
          initial={editInvestor}
          initialContact={contacts.find(c => c.id === editInvestor.contact_id)}
          onSave={(_contact, invData) => { onUpdate(editInvestor.id, invData); setEditInvestor(null); }}
          onClose={() => setEditInvestor(null)}
        />
      )}
    </div>
  );
}

// ─── Supabase helpers ───
function dealFromDB(row) {
  return {
    id: row.id,
    address: row.address,
    price: row.price,
    offerPrice: row.offer_price,
    arv: row.arv,
    repair: row.repair,
    profit: row.profit,
    score: row.score,
    type: row.type,
    strategy: row.strategy,
    yield: row.yield,
    monthlyRent: row.monthly_rent,
    pipelineStatus: normalizeStage(row.pipeline_status),
    notes: row.notes || "",
    savedAt: row.saved_at,
    propertyId: row.property_id,
    property: row.properties || null,
    vendorContactId: row.vendor_contact_id,
    agentContactId: row.agent_contact_id,
    solicitorId: row.solicitor_id,
    brokerContactId: row.broker_contact_id,
  };
}

function dealToDB(deal, userId) {
  return {
    user_id: userId,
    address: deal.address,
    price: deal.price,
    offer_price: deal.offerPrice,
    arv: deal.arv,
    repair: deal.repair,
    profit: deal.profit,
    score: deal.score,
    type: deal.type,
    strategy: deal.strategy,
    yield: deal.yield,
    monthly_rent: deal.monthlyRent,
    pipeline_status:    deal.pipelineStatus || "researching",
    notes:              deal.notes || "",
    property_id:        deal.propertyId     || null,
    vendor_contact_id:  deal.vendorContactId  || null,
    agent_contact_id:   deal.agentContactId   || null,
    solicitor_id:       deal.solicitorId      || null,
    broker_contact_id:  deal.brokerContactId  || null,
  };
}

// ─── Login Screen ───
function LoginScreen() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showPw, setShowPw] = useState(false);

  const submit = async () => {
    setError(""); setMessage(""); setLoading(true);
    try {
      if (mode === "signup") {
        const { error: e } = await supabase.auth.signUp({ email, password });
        if (e) throw e;
        setMessage("Check your email to confirm your account.");
      } else {
        const { error: e } = await supabase.auth.signInWithPassword({ email, password });
        if (e) throw e;
      }
    } catch (e) {
      setError(e.message || "Authentication failed");
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center"
      style={{
        background: "linear-gradient(170deg, #1a1b2e 0%, #1f2138 40%, #1a1b2e 100%)",
        fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@700;800&family=Dancing+Script:wght@600;700&display=swap');`}</style>
      <div className="w-full max-w-sm mx-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-[#faf8ff]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Deal Finder
          </h1>
          <p className="text-sm text-[#6a6a82] mt-2">AI-powered property investment intelligence</p>
        </div>
        <div className="bg-[#26273a] border border-[#3e4060] rounded-2xl p-6 shadow-2xl space-y-5">
          <div className="flex gap-1 bg-[#1a1b2e] rounded-xl p-1">
            {["login", "signup"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); setMessage(""); }}
                className="flex-1 py-2 rounded-lg text-xs font-bold tracking-wider uppercase transition-all"
                style={{
                  background: mode === m ? "linear-gradient(135deg, #c9a227, #d4af37)" : "transparent",
                  color: mode === m ? "#171829" : "#a2a2c0",
                }}
              >
                {m === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            <Field label="Email" value={email} onChange={setEmail} placeholder="you@email.com" type="email" />
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold tracking-[.15em] uppercase text-[#b8b8d0]">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#1f2135] border border-[#3e4060] rounded-lg px-4 py-3 pr-11 text-[#f5f4ff] placeholder-[#444] text-sm focus:outline-none focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a22744] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a6a82] hover:text-[#c9a227] transition-colors text-sm"
                  tabIndex={-1}
                >
                  {showPw ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
          </div>
          {error && <div className="text-xs text-[#f87171] bg-[#f8717111] border border-[#f8717133] rounded-lg px-3 py-2">{error}</div>}
          {message && <div className="text-xs text-[#4ade80] bg-[#4ade8011] border border-[#4ade8033] rounded-lg px-3 py-2">{message}</div>}
          <GoldButton onClick={submit} loading={loading} loadingLabel="Signing in…" label={mode === "login" ? "Log In" : "Create Account"} />
          <p className="text-[10px] text-center text-[#50516e]">
            Your deals are stored securely per account. API keys stay in your browser only.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main App ───
export default function PropertyDealFinder() {
  const [tab, setTab] = useState("dashboard");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [apiKey, setApiKey] = useState(() => {
    try { return localStorage.getItem("pf_apiKey") || process.env.REACT_APP_ANTHROPIC_KEY || ""; } catch { return ""; }
  });
  const [epcEmail, setEpcEmail] = useState(() => {
    try { return localStorage.getItem("pf_epcEmail") || process.env.REACT_APP_EPC_EMAIL || ""; } catch { return ""; }
  });
  const [epcKey, setEpcKey] = useState(() => {
    try { return localStorage.getItem("pf_epcKey") || process.env.REACT_APP_EPC_KEY || ""; } catch { return ""; }
  });
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Persist API keys locally (personal credentials, not app data)
  useEffect(() => { try { localStorage.setItem("pf_apiKey", apiKey); } catch {} }, [apiKey]);
  useEffect(() => { try { localStorage.setItem("pf_epcEmail", epcEmail); } catch {} }, [epcEmail]);
  useEffect(() => { try { localStorage.setItem("pf_epcKey", epcKey); } catch {} }, [epcKey]);

  // Auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setAuthLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  const userId = user?.id;

  // Load deals from Supabase (join properties for display)
  useEffect(() => {
    if (!userId) return;
    supabase.from("deals").select("*, properties(id, address_line_1, address_line_2, city, postcode, property_type, bedrooms, bathrooms, sq_ft, tenure, epc_rating)")
      .eq("user_id", userId)
      .order("saved_at", { ascending: false })
      .then(({ data }) => setDeals((data || []).map(dealFromDB)));
  }, [userId]);

  // Load properties from Supabase
  useEffect(() => {
    if (!userId) return;
    supabase.from("properties").select("*").eq("user_id", userId).eq("archived", false).order("created_at", { ascending: false })
      .then(({ data }) => setProperties(data || []));
  }, [userId]);

  // Load contacts
  useEffect(() => {
    if (!userId) return;
    supabase.from("contacts").select("*").eq("user_id", userId).eq("archived", false).order("created_at", { ascending: false })
      .then(({ data }) => setContacts(data || []));
  }, [userId]);

  // Load portfolio
  useEffect(() => {
    if (!userId) return;
    supabase.from("portfolio").select("*, properties(address_line_1, address_line_2, city, postcode, property_type, bedrooms)").eq("user_id", userId).eq("archived", false).order("created_at", { ascending: false })
      .then(({ data }) => setPortfolio(data || []));
  }, [userId]);

  // Load investors
  useEffect(() => {
    if (!userId) return;
    supabase.from("investors").select("*").eq("user_id", userId).eq("archived", false).order("created_at", { ascending: false })
      .then(({ data }) => setInvestors(data || []));
  }, [userId]);

  const saveDeal = useCallback(async (deal) => {
    const tempId = crypto.randomUUID();
    const newDeal = { ...deal, id: tempId, pipelineStatus: "researching", notes: "", savedAt: new Date().toISOString() };
    setDeals((prev) => [newDeal, ...prev]);
    const { data, error } = await supabase.from("deals").insert(dealToDB(newDeal, userId)).select().single();
    if (error) { setDeals((prev) => prev.filter((d) => d.id !== tempId)); alert("Failed to save deal: " + error.message); return; }
    if (data) setDeals((prev) => prev.map((d) => d.id === tempId ? dealFromDB(data) : d));
  }, [userId]);

  const removeDeal = useCallback(async (id) => {
    if (!window.confirm("Delete this deal? This cannot be undone.")) return;
    const { error } = await supabase.from("deals").delete().eq("id", id);
    if (error) { alert("Failed to delete deal: " + error.message); return; }
    setDeals((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const updateDeal = useCallback(async (id, fields) => {
    setDeals((prev) => prev.map((d) => d.id === id ? { ...d, ...fields } : d));
    const dbFields = {};
    if (fields.address !== undefined)     dbFields.address       = fields.address;
    if (fields.price !== undefined)       dbFields.price         = fields.price;
    if (fields.offerPrice !== undefined)  dbFields.offer_price   = fields.offerPrice;
    if (fields.arv !== undefined)         dbFields.arv           = fields.arv;
    if (fields.repair !== undefined)      dbFields.repair        = fields.repair;
    if (fields.monthlyRent !== undefined) dbFields.monthly_rent  = fields.monthlyRent;
    if (fields.strategy !== undefined)    dbFields.strategy      = fields.strategy;
    if (fields.type !== undefined)        dbFields.type          = fields.type;
    if (fields.pipelineStatus !== undefined) dbFields.pipeline_status = fields.pipelineStatus;
    if (fields.notes !== undefined)       dbFields.notes         = fields.notes;
    if (fields.score !== undefined)            dbFields.score             = fields.score;
    if (fields.propertyId !== undefined)       dbFields.property_id       = fields.propertyId;
    if (fields.vendorContactId !== undefined)  dbFields.vendor_contact_id = fields.vendorContactId;
    if (fields.agentContactId !== undefined)   dbFields.agent_contact_id  = fields.agentContactId;
    if (fields.solicitorId !== undefined)      dbFields.solicitor_id      = fields.solicitorId;
    if (fields.brokerContactId !== undefined)  dbFields.broker_contact_id = fields.brokerContactId;
    await supabase.from("deals").update(dbFields).eq("id", id);
  }, []);

  const updateDealStatus = useCallback(async (id, status) => {
    const prev = deals.find((d) => d.id === id)?.pipelineStatus;
    setDeals((ds) => ds.map((d) => d.id === id ? { ...d, pipelineStatus: status } : d));
    const { error } = await supabase.from("deals").update({ pipeline_status: status }).eq("id", id);
    if (error) {
      console.error("updateDealStatus:", error.message);
      setDeals((ds) => ds.map((d) => d.id === id ? { ...d, pipelineStatus: prev } : d));
      alert("Failed to move deal: " + error.message);
    }
  }, [deals]);

  const updateDealNotes = useCallback(async (id, notes) => {
    setDeals((prev) => prev.map((d) => d.id === id ? { ...d, notes } : d));
    const { error } = await supabase.from("deals").update({ notes }).eq("id", id);
    if (error) { console.error("updateDealNotes:", error.message); alert("Failed to save notes: " + error.message); }
  }, []);

  const clearDeals = useCallback(async () => {
    setDeals([]);
    if (userId) await supabase.from("deals").delete().eq("user_id", userId);
  }, [userId]);

  const addContact = useCallback(async (data) => {
    const { data: c } = await supabase.from("contacts").insert({ ...data, user_id: userId }).select().single();
    if (c) setContacts(prev => [c, ...prev]);
  }, [userId]);

  const updateContact = useCallback(async (id, data) => {
    const { id: _id, user_id: _uid, created_at: _ca, updated_at: _ua, ...fields } = data;
    const { error } = await supabase.from("contacts").update(fields).eq("id", id);
    if (error) { alert("Failed to update contact: " + error.message); return; }
    setContacts(prev => prev.map(c => c.id === id ? { ...c, ...fields } : c));
  }, []);

  const deleteContact = useCallback(async (id) => {
    setContacts(prev => prev.filter(c => c.id !== id));
    await supabase.from("contacts").delete().eq("id", id);
  }, []);

  const archiveContact = useCallback(async (id) => {
    setContacts(prev => prev.filter(c => c.id !== id));
    await supabase.from("contacts").update({ archived: true }).eq("id", id);
  }, []);

  const addProperty = useCallback(async (propertyData) => {
    const { data: prop, error } = await supabase.from("properties").insert({ ...propertyData, user_id: userId }).select().single();
    if (error) { alert("Failed to save property: " + error.message); return null; }
    if (prop) setProperties(prev => [prop, ...prev]);
    return prop;
  }, [userId]);

  const addPortfolio = useCallback(async (portfolioData) => {
    const clean = Object.fromEntries(Object.entries(portfolioData).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: entry, error } = await supabase.from("portfolio").insert({ ...clean, user_id: userId }).select("*, properties(address_line_1, address_line_2, city, postcode, property_type, bedrooms)").single();
    if (error) { alert("Failed to save portfolio entry: " + error.message); return; }
    if (entry) setPortfolio(prev => [entry, ...prev]);
  }, [userId]);

  const updatePortfolio = useCallback(async (id, portfolioData) => {
    const clean = Object.fromEntries(Object.entries(portfolioData).map(([k, v]) => [k, v === "" ? null : v]));
    const { data: row } = await supabase.from("portfolio").update(clean).eq("id", id)
      .select("*, properties(address_line_1, address_line_2, city, postcode, property_type, bedrooms)").single();
    if (row) setPortfolio(prev => prev.map(p => p.id === id ? row : p));
  }, []);

  const deletePortfolio = useCallback(async (id) => {
    setPortfolio(prev => prev.filter(p => p.id !== id));
    await supabase.from("portfolio").delete().eq("id", id);
  }, []);

  const addInvestor = useCallback(async (contactData, investorData) => {
    const { data: contact } = await supabase.from("contacts").insert({ ...contactData, contact_type: "investor", user_id: userId }).select().single();
    if (!contact) return;
    setContacts(prev => [contact, ...prev]);
    const { data: inv } = await supabase.from("investors").insert({ ...investorData, contact_id: contact.id, user_id: userId }).select().single();
    if (inv) setInvestors(prev => [inv, ...prev]);
  }, [userId]);

  const updateInvestor = useCallback(async (id, data) => {
    const { data: row } = await supabase.from("investors").update(data).eq("id", id).select().single();
    if (row) setInvestors(prev => prev.map(i => i.id === id ? { ...i, ...row } : i));
  }, []);

  const deleteInvestor = useCallback(async (id) => {
    setInvestors(prev => prev.filter(i => i.id !== id));
    await supabase.from("investors").delete().eq("id", id);
  }, []);

  const currentTab = TABS.find((t) => t.id === tab);

  if (authLoading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#1a1b2e" }}>
      <div className="text-[#c9a227] text-sm">Loading…</div>
    </div>
  );
  if (!user) return <LoginScreen />;

  return (
    <div
      className="min-h-screen w-full flex overflow-x-hidden"
      style={{
        background: "linear-gradient(170deg, #1a1b2e 0%, #1f2138 40%, #1a1b2e 100%)",
        fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@700;800&family=Dancing+Script:wght@600;700&display=swap');
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
        .animate-fadeIn { animation: fadeIn .35s ease-out }
        @keyframes shimmer { 0% { background-position:-200% 0 } 100% { background-position:200% 0 } }
        .gold-shimmer {
          background: linear-gradient(90deg, #c9a227 0%, #f5e6a3 25%, #c9a227 50%, #f5e6a3 75%, #c9a227 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 4s linear infinite;
        }
        select option { background: #1f2135; color: #f5f4ff; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #3e4060; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #565882; }
      `}</style>

      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-[#161724] border-r border-[#2e3044] flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="px-5 py-6 border-b border-[#2e3044]">
          <h1 className="text-xl font-extrabold text-[#faf8ff]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Deal <span className="gold-shimmer">Finder</span>
          </h1>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSidebarOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
              style={{
                background: tab === t.id ? "linear-gradient(135deg, #2e3044, #373958)" : "transparent",
                color: tab === t.id ? "#c9a227" : "#a2a2c0",
                boxShadow: tab === t.id ? "0 2px 12px rgba(0,0,0,0.2)" : "none",
              }}
            >
              <span className="text-lg w-7 text-center">{t.icon}</span>
              <span>{t.label}</span>
              {t.id === "dashboard" && deals.length > 0 && (
                <span className="ml-auto text-[10px] font-bold bg-[#c9a22722] text-[#c9a227] px-2 py-0.5 rounded-full">{deals.length}</span>
              )}
            </button>
          ))}
        </nav>

        {/* Settings */}
        <div className="px-3 pb-4 border-t border-[#2e3044] pt-3 space-y-2">
          <button
            onClick={() => setShowSettings(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#a2a2c0] hover:text-[#c4c4da] hover:bg-[#252737] transition-all"
          >
            <span className="text-lg w-7 text-center">⚙️</span>
            <span>Settings</span>
            {!apiKey && (
              <span className="ml-auto text-[9px] font-bold bg-[#f8717122] text-[#f87171] px-2 py-0.5 rounded-full">No API Key</span>
            )}
          </button>
          <button
            onClick={() => supabase.auth.signOut()}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#a2a2c0] hover:text-[#f87171] hover:bg-[#f8717111] transition-all"
          >
            <span className="text-lg w-7 text-center">🚪</span>
            <span>Sign Out</span>
          </button>
          <div className="px-3 text-[10px] text-[#50516e]">v3.0 · AI-powered property intelligence</div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen min-w-0 overflow-x-hidden">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-[#1a1b2e]/80 backdrop-blur-md border-b border-[#2e3044] px-4 lg:px-8 py-3 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[#a2a2c0] hover:text-[#c4c4da] text-xl">☰</button>
          <div className="flex items-center gap-2">
            <span className="text-lg">{currentTab?.icon}</span>
            <h2 className="text-sm font-bold text-[#c4c4da] tracking-wide">{currentTab?.label}</h2>
          </div>
          {!apiKey && (
            <button
              onClick={() => setShowSettings(true)}
              className="ml-auto text-[11px] font-bold px-3 py-1.5 rounded-lg bg-[#c9a22722] text-[#c9a227] border border-[#c9a22733] hover:bg-[#c9a22733] transition-colors"
            >
              Add API Key to Enable AI
            </button>
          )}
        </div>

        {/* Page content */}
        <div className={`${tab === "dashboard" ? "w-full px-4 lg:px-6" : "max-w-4xl mx-auto px-4 lg:px-8"} py-8 animate-fadeIn`} key={tab}>
          {tab === "dashboard" && (
            <Dashboard
              deals={deals}
              userId={userId}
              onNavigate={setTab}
              onRemoveDeal={removeDeal}
              onUpdateStatus={updateDealStatus}
              onUpdateNotes={updateDealNotes}
            />
          )}
          {tab === "deals" && <DealsTab deals={deals} onEdit={updateDeal} onRemove={removeDeal} onSaveDeal={saveDeal} apiKey={apiKey} epcEmail={epcEmail} epcKey={epcKey} contacts={contacts} properties={properties} onCreateProperty={addProperty} />}
          {tab === "scout" && <MarketScout apiKey={apiKey} />}
          {tab === "calc" && <Calculators />}
          {tab === "valuation" && <ValuationTab apiKey={apiKey} deals={deals} userId={userId} />}
          {tab === "auction" && <AuctionPrep apiKey={apiKey} />}
          {tab === "contacts" && <ContactsTab contacts={contacts} onAdd={addContact} onUpdate={updateContact} onDelete={deleteContact} onArchive={archiveContact} apiKey={apiKey} />}
          {tab === "portfolio" && <PortfolioTab portfolio={portfolio} userId={userId} onAdd={addPortfolio} onDelete={deletePortfolio} onUpdate={updatePortfolio} investors={investors} contacts={contacts} deals={deals} />}
          {tab === "investors" && <InvestorsTab investors={investors} contacts={contacts} userId={userId} onAdd={addInvestor} onDelete={deleteInvestor} onUpdate={updateInvestor} />}
        </div>

        <div className="text-center pb-8 px-4">
          <p className="text-[10px] text-[#50516e] max-w-lg mx-auto">
            AI-generated analysis for informational purposes only. Always conduct your own due diligence, verify all figures independently, and consult qualified professionals (solicitors, surveyors, financial advisors) before making investment decisions.
          </p>
        </div>
      </main>

      {showSettings && <SettingsModal apiKey={apiKey} setApiKey={setApiKey} epcEmail={epcEmail} setEpcEmail={setEpcEmail} epcKey={epcKey} setEpcKey={setEpcKey} onClose={() => setShowSettings(false)} deals={deals} onClearDeals={clearDeals} />}
    </div>
  );
}